import * as THREE from 'three';
import type { DuckNetState, GameEvent, PlayerColor, PlayerNetState, ServerMessage } from '../../shared/protocol';
import { getScaledEnemyCount, normalizePlayerCount } from './balance';
import { Duck, type DuckKind } from './Duck';
import { calculateScore, DIFFICULTIES, getDuckKindForSpawn, getHordeComposition, type Difficulty, type GameMode } from './content';
import {
  DOOR_BY_CELL,
  getKeyAmbushCells,
  getReinforcementCells,
  HORDE_LEVEL,
  KEY_BY_CELL,
  LEVELS,
  type KeyColor,
  type LevelDefinition,
} from './levels';
import { NetworkClient } from './multiplayer';
import { buildNavigationField, getNavigationStep } from './navigation';
import { RetroAudio } from './audio';
import { SplashParticles } from './SplashParticles';
import { canReceiveDamage, isPlayerTargetable, type RunStatus } from './sessionRules';
import { createPlayerModel } from './PlayerModel';

const CELL = 4;
const WALL_HEIGHT = 3.6;
const EYE_HEIGHT = 1.62;
const PLAYER_RADIUS = 0.48;
const MAX_STAMINA = 100;
const SPRINT_DRAIN = 30;
const SPRINT_RECHARGE = 21;
const DUCK_SEPARATION_RADIUS = 2.7;
const DUCK_BUCKET_SIZE = DUCK_SEPARATION_RADIUS;
const DUCK_BUCKET_STRIDE = 4096;
const DUCK_RENDER_DISTANCE_SQ = 60 * 60;
const DUCK_DETAIL_DISTANCE_SQ = 32 * 32;

const duckBucketKey = (bucketX: number, bucketZ: number): number =>
  bucketX * DUCK_BUCKET_STRIDE + bucketZ;

type Weapon = {
  mode: 'pump' | 'bubble' | 'roll' | 'shower';
  name: string;
  damage: number;
  cooldown: number;
  magazine: number;
  magazineSize: number;
  reserve: number;
  maxReserve: number;
  reloadSeconds: number;
  color: number;
  range: number;
  recoil: number;
};

type DoorState = {
  id: string;
  row: number;
  col: number;
  color: KeyColor;
  mesh: THREE.Mesh;
  open: boolean;
  progress: number;
};

type PickupState = {
  id: string;
  row: number;
  col: number;
  kind: 'key' | 'health' | 'ammo';
  key?: KeyColor;
  mesh: THREE.Group;
  active: boolean;
};

type PuddleState = {
  position: THREE.Vector2;
  radius: number;
  mesh: THREE.Mesh;
  bubbles: THREE.Mesh[];
  phase: number;
};

type SecretState = {
  id: string;
  row: number;
  col: number;
  mesh: THREE.Mesh;
  open: boolean;
  progress: number;
};

type ElevatorState = {
  id: string;
  position: THREE.Vector3;
  mesh: THREE.Group;
};

type TrapState = {
  position: THREE.Vector2;
  mesh: THREE.Group;
  signal: THREE.MeshStandardMaterial;
  phase: number;
};

type RemotePlayer = {
  state: PlayerNetState;
  group: THREE.Group;
};

export type GameSettings = {
  sensitivity: number;
  volume: number;
  reducedMotion: boolean;
  difficulty: Difficulty;
};

export type MinimapState = {
  grid: string[];
  explored: Uint8Array;
  revision: number;
  player: { row: number; col: number; yaw: number };
  doors: Array<{ row: number; col: number; color: KeyColor; open: boolean }>;
  secrets: Array<{ row: number; col: number; open: boolean }>;
  keys: Array<{ row: number; col: number; color: KeyColor }>;
  teammates: Array<{ row: number; col: number; color: PlayerColor }>;
};

export type HudState = {
  health: number;
  weapon: string;
  ammo: string;
  stamina: number;
  movementState: 'ready' | 'sprinting' | 'tired' | 'slowed';
  reloading: boolean;
  keys: KeyColor[];
  ducksLeft: number;
  objective: string;
  mapIndex: number;
  mode: GameMode;
  wave: number;
  elapsedSeconds: number;
  score: number;
  interaction?: { key?: 'E'; label: string; blocked?: boolean };
  boss?: { label: string; health: number; maxHealth: number };
  teammates: Array<{ id: string; nickname: string; color: PlayerColor; health: number; paused: boolean }>;
  minimap?: MinimapState;
};

export type DamageDirection = 'front' | 'right' | 'back' | 'left' | 'environment';

export const classifyDamageDirection = (yaw: number, deltaX: number, deltaZ: number): DamageDirection => {
  if (Math.hypot(deltaX, deltaZ) < 0.001) return 'environment';
  const forwardDot = deltaX * -Math.sin(yaw) + deltaZ * -Math.cos(yaw);
  const rightDot = deltaX * Math.cos(yaw) + deltaZ * -Math.sin(yaw);
  if (Math.abs(forwardDot) >= Math.abs(rightDot)) return forwardDot >= 0 ? 'front' : 'back';
  return rightDot >= 0 ? 'right' : 'left';
};

const COOPERATIVE_SPAWN_OFFSETS: ReadonlyArray<readonly [number, number]> = [
  [-0.8, -0.8],
  [0.8, -0.8],
  [-0.8, 0.8],
  [0.8, 0.8],
];

export const getCooperativeSpawnOffset = (playerIndex: number): readonly [number, number] =>
  COOPERATIVE_SPAWN_OFFSETS[Math.max(0, playerIndex) % COOPERATIVE_SPAWN_OFFSETS.length];

export type GameResult = {
  victory: boolean;
  elapsedSeconds: number;
  kills: number;
  secrets: number;
  revives: number;
  score: number;
  difficulty: Difficulty;
  mode: GameMode;
  wave: number;
  damageTaken: number;
};

export type GameCallbacks = {
  onHud: (state: HudState) => void;
  onToast: (message: string) => void;
  onLevel: (level: LevelDefinition, index: number) => void;
  onDamage: (direction: DamageDirection) => void;
  onHit: () => void;
  onPause: (actuallyPaused: boolean) => void;
  onResult: (result: GameResult) => void;
};

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(72, 1, 0.05, 160);
  private readonly timer = new THREE.Timer();
  private readonly raycaster = new THREE.Raycaster();
  private readonly keysDown = new Set<string>();
  private readonly audio = new RetroAudio();
  private readonly particles = new SplashParticles();
  private readonly doors = new Map<string, DoorState>();
  private readonly pickups = new Map<string, PickupState>();
  private readonly ducks = new Map<string, Duck>();
  private readonly remotePlayers = new Map<string, RemotePlayer>();
  private readonly keysOwned = new Set<KeyColor>();
  private readonly worldRaycast: THREE.Object3D[] = [];
  private readonly puddles: PuddleState[] = [];
  private readonly secrets = new Map<string, SecretState>();
  private readonly elevators: ElevatorState[] = [];
  private readonly traps: TrapState[] = [];
  private readonly animatedFixtureMaterials: THREE.MeshStandardMaterial[] = [];
  private readonly networkDisposers: Array<() => void> = [];
  private readonly velocity = new THREE.Vector3();
  private readonly spawn = new THREE.Vector3();
  private readonly duckPathDirection = new THREE.Vector3();
  private readonly duckLateral = new THREE.Vector3();
  private readonly duckSeparation = new THREE.Vector3();
  private readonly duckSteeringTarget = new THREE.Vector3();
  private readonly duckSpatialBuckets = new Map<number, Duck[]>();
  private readonly weaponRig = new THREE.Group();
  private minimapExplored = new Uint8Array();
  private minimapRevision = 0;
  private readonly weapons: Weapon[] = [
    { mode: 'pump', name: 'STURALAVANDINI', damage: 19, cooldown: 0.72, magazine: 6, magazineSize: 6, reserve: 30, maxReserve: 36, reloadSeconds: 1.65, color: 0xe46628, range: 17, recoil: 1 },
    { mode: 'bubble', name: 'SPARABOLLE', damage: 48, cooldown: 0.4, magazine: 8, magazineSize: 8, reserve: 40, maxReserve: 48, reloadSeconds: 1.72, color: 0x73d9f0, range: 48, recoil: 0.48 },
    { mode: 'roll', name: 'LANCIACARTA', damage: 112, cooldown: 0.98, magazine: 2, magazineSize: 2, reserve: 12, maxReserve: 14, reloadSeconds: 2.08, color: 0xd9eeee, range: 52, recoil: 1.15 },
    { mode: 'shower', name: 'SOFFIONE TURBO', damage: 15, cooldown: 0.12, magazine: 42, magazineSize: 42, reserve: 168, maxReserve: 210, reloadSeconds: 1.48, color: 0x58c9d4, range: 20, recoil: 0.28 },
  ];

  private level?: LevelDefinition;
  private levelIndex = 0;
  private health = 100;
  private activeWeapon = 0;
  private nextShotAt = 0;
  private startedAt = 0;
  private kills = 0;
  private status: RunStatus = 'idle';
  private paused = false;
  private online = false;
  private network?: NetworkClient;
  private exit?: THREE.Group;
  private elapsed = 0;
  private lastNetworkTick = 0;
  private lastDuckTick = 0;
  private recoil = 0;
  private toastCooldown = 0;
  private exitRequested = false;
  private playerCount = 1;
  private mode: GameMode = 'campaign';
  private stamina = MAX_STAMINA;
  private staminaRechargeDelay = 0;
  private sprinting = false;
  private slowedByPuddle = false;
  private nextFootSplashAt = 0;
  private elevatorCooldown = 0;
  private trapDamageCooldown = 0;
  private secretsFound = 0;
  private revives = 0;
  private reviveDeadline = 0;
  private hordeWave = 0;
  private nextHordeWaveAt = 0;
  private damageTaken = 0;
  private nextAmbientSteamAt = 0;
  private reloadEndsAt = 0;
  private reloadingWeapon = -1;
  private navField?: Int16Array;
  private nextNavRefreshAt = 0;
  private nextHudRefreshAt = 0;
  private nextMusicRefreshAt = 0;
  private renderScale = 0.82;
  private smoothedFrameTime = 1 / 60;
  private nextQualityCheckAt = 0;
  private settings: GameSettings;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly callbacks: GameCallbacks,
    settings: GameSettings,
  ) {
    this.settings = settings;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.34;
    this.renderScale = window.innerWidth >= 1600 ? 0.78 : window.innerWidth < 1000 ? 0.72 : 0.84;
    this.nextQualityCheckAt = performance.now() / 1000 + 4;
    this.camera.rotation.order = 'YXZ';
    this.camera.add(this.weaponRig);
    this.timer.connect(document);
    this.bindInput();
    this.resize();
    this.renderer.setAnimationLoop((timestamp) => this.frame(timestamp));
  }

  updateSettings(settings: GameSettings): void {
    this.settings = settings;
    this.audio.setVolume(settings.volume);
  }

  startSolo(mode: GameMode = 'campaign'): void {
    this.detachNetwork();
    this.online = false;
    this.playerCount = 1;
    this.mode = mode;
    this.beginRun();
  }

  startOnline(network: NetworkClient, mode: GameMode = 'campaign'): void {
    this.detachNetwork();
    this.online = true;
    this.network = network;
    this.playerCount = normalizePlayerCount(network.players.length);
    this.mode = mode;
    this.attachNetwork(network);
    this.beginRun();
  }

  resume(): void {
    if (this.status !== 'playing') return;
    this.paused = false;
    this.timer.reset();
    void this.canvas.requestPointerLock();
  }

  quit(): void {
    this.status = 'idle';
    this.paused = false;
    this.keysDown.clear();
    this.audio.stopMusic();
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    this.detachNetwork();
    this.clearWorld();
    this.minimapExplored = new Uint8Array();
  }

  requestPointerLock(): void {
    if (this.status === 'playing') void this.canvas.requestPointerLock();
  }

  private beginRun(): void {
    this.health = 100;
    this.kills = 0;
    this.secretsFound = 0;
    this.revives = 0;
    this.reviveDeadline = 0;
    this.hordeWave = 0;
    this.nextHordeWaveAt = 0;
    this.damageTaken = 0;
    this.startedAt = performance.now();
    this.status = 'playing';
    this.paused = false;
    for (const weapon of this.weapons) {
      weapon.magazine = weapon.magazineSize;
      weapon.reserve = Math.round(weapon.maxReserve * (DIFFICULTIES[this.settings.difficulty] ?? DIFFICULTIES.normal).ammo);
    }
    this.stamina = MAX_STAMINA;
    this.staminaRechargeDelay = 0;
    this.sprinting = false;
    this.reloadEndsAt = 0;
    this.reloadingWeapon = -1;
    this.activeWeapon = 0;
    this.audio.setVolume(this.settings.volume);
    void this.audio.init().then(() => this.audio.startMusic(0));
    this.loadLevel(0);
    this.requestPointerLock();
  }

  private loadLevel(index: number): void {
    const level = this.mode === 'horde' ? HORDE_LEVEL : LEVELS[index];
    if (!level) {
      this.finish(true);
      return;
    }
    this.levelIndex = index;
    this.level = level;
    this.exitRequested = false;
    this.keysOwned.clear();
    this.clearWorld();
    this.minimapExplored = new Uint8Array(level.grid.length * level.grid[0].length);
    this.minimapRevision += 1;
    this.scene.background = new THREE.Color(level.fogColor);
    this.scene.fog = new THREE.FogExp2(level.fogColor, 0.017);
    this.audio.setMusicState(index, 0);
    this.buildLighting(level);
    this.buildArchitecture(level);
    this.buildWeapon();
    this.camera.position.copy(this.getPlayerSpawnPosition());
    this.camera.rotation.set(0, 0, 0);
    this.revealMinimap(this.camera.position, 5);
    this.velocity.set(0, 0, 0);
    this.health = Math.min(100, this.health + 20);
    this.stamina = MAX_STAMINA;
    this.navField = undefined;
    this.nextNavRefreshAt = 0;
    this.callbacks.onLevel(level, index);
    this.updateHud();
  }

  private clearWorld(): void {
    for (const child of [...this.scene.children]) this.scene.remove(child);
    this.scene.add(this.camera);
    this.doors.clear();
    this.pickups.clear();
    this.ducks.clear();
    this.remotePlayers.clear();
    this.worldRaycast.length = 0;
    this.puddles.length = 0;
    this.secrets.clear();
    this.elevators.length = 0;
    this.traps.length = 0;
    this.animatedFixtureMaterials.length = 0;
    this.exit = undefined;
    this.weaponRig.clear();
    this.particles.reset();
  }

  private getPlayerSpawnPosition(): THREE.Vector3 {
    if (!this.online || !this.network) return this.spawn.clone();
    const resolvedIndex = this.network.players.findIndex((player) => player.id === this.network?.id);
    const playerIndex = resolvedIndex >= 0 ? resolvedIndex : this.network.isHost ? 0 : 1;
    const [offsetX, offsetZ] = getCooperativeSpawnOffset(playerIndex);
    const position = this.spawn.clone();
    position.x += offsetX;
    position.z += offsetZ;
    return this.isBlocked(position, PLAYER_RADIUS) ? this.spawn.clone() : position;
  }

  private buildLighting(level: LevelDefinition): void {
    const width = level.grid[0].length * CELL;
    const depth = level.grid.length * CELL;
    const shadowSpan = Math.max(width, depth) * 0.52;
    const ambient = new THREE.HemisphereLight(0xc8f5f1, 0x3d2518, 2.15);
    this.scene.add(ambient);
    const main = new THREE.DirectionalLight(0xffd18c, 2.45);
    main.position.set(-12, 18, 8);
    main.castShadow = true;
    main.shadow.mapSize.set(1024, 1024);
    main.shadow.camera.left = -shadowSpan;
    main.shadow.camera.right = shadowSpan;
    main.shadow.camera.top = shadowSpan;
    main.shadow.camera.bottom = -shadowSpan;
    main.shadow.camera.far = 280;
    this.scene.add(main);

    const fill = new THREE.DirectionalLight(0x5bd9e5, 0.9);
    fill.position.set(16, 10, -12);
    this.scene.add(fill);

    // Point lights are particularly expensive in a forward renderer: every
    // one is evaluated on every lit fragment. Three broad accents preserve
    // the colour rhythm without making large rooms stutter.
    for (let index = 0; index < 3; index += 1) {
      const color = level.lightColors[index % level.lightColors.length];
      const light = new THREE.PointLight(color, 16, 42, 2);
      light.position.set(
        (index - 1) * width * 0.27,
        4.5,
        (index - 1) * depth * 0.25,
      );
      this.scene.add(light);
    }
  }

  private buildArchitecture(level: LevelDefinition): void {
    const height = level.grid.length;
    const width = level.grid[0].length;
    const wallMaterials = [level.wallHue, level.wallAccent, level.tileHue].map((color, index) =>
      new THREE.MeshStandardMaterial({
        map: this.createIndustrialTexture(color, true),
        emissive: color,
        emissiveIntensity: index === 2 ? 0.08 : 0.035,
        roughness: index === 2 ? 0.56 : 0.72,
        metalness: index === 2 ? 0.12 : 0.34,
      }),
    );
    const floorTexture = this.createIndustrialTexture(level.floorHue, false);
    floorTexture.repeat.set(width / 2, height / 2);
    const wallGeometry = new THREE.BoxGeometry(CELL, WALL_HEIGHT, CELL);

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(width * CELL, height * CELL),
      new THREE.MeshStandardMaterial({ map: floorTexture, emissive: level.floorHue, emissiveIntensity: 0.045, roughness: 0.68, metalness: 0.16 }),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const ceilingTexture = this.createIndustrialTexture(level.wallHue, true);
    ceilingTexture.repeat.set(width / 3, height / 3);
    const ceiling = new THREE.Mesh(
      new THREE.PlaneGeometry(width * CELL, height * CELL),
      new THREE.MeshStandardMaterial({
        map: ceilingTexture,
        color: 0x677274,
        emissive: level.wallHue,
        emissiveIntensity: 0.12,
        roughness: 0.76,
        metalness: 0.28,
        side: THREE.DoubleSide,
      }),
    );
    ceiling.position.y = WALL_HEIGHT;
    ceiling.rotation.x = Math.PI / 2;
    this.scene.add(ceiling);
    this.addCeilingGrid(level, width, height);

    const wallInstances: THREE.Vector3[][] = [[], [], []];
    let duckCount = 0;
    for (let row = 0; row < height; row += 1) {
      for (let col = 0; col < width; col += 1) {
        const cell = level.grid[row][col];
        const world = this.cellToWorld(row, col, width, height);
        if (cell === '#') {
          const materialIndex = ((Math.floor(col / 8) + Math.floor(row / 7) + this.levelIndex) % 5 === 0)
            ? 2
            : ((row * 17 + col * 7 + this.levelIndex) % 11 === 0 ? 1 : 0);
          wallInstances[materialIndex].push(new THREE.Vector3(world.x, WALL_HEIGHT / 2, world.z));
          if ((row * 7 + col * 3) % 19 === 0) this.addPipe(world.x, world.z, row === 0 || row === height - 1);
          if ((row * 13 + col * 5 + this.levelIndex) % 23 === 0) this.addWallTrim(world, level.tileHue);
          continue;
        }
        if (cell === 'S') {
          this.spawn.set(world.x, EYE_HEIGHT, world.z);
        } else if (cell === 'd' || cell === 'q' || cell === 'B') {
          const kind = getDuckKindForSpawn(duckCount + (cell === 'q' ? 3 : 0), this.levelIndex, cell === 'B');
          this.addDuck(`duck-${this.levelIndex}-${duckCount}`, kind, world);
          duckCount += 1;
        } else if (KEY_BY_CELL[cell]) {
          this.addPickup(row, col, world, 'key', KEY_BY_CELL[cell]);
        } else if (cell === 'H') {
          this.addPickup(row, col, world, 'health');
        } else if (cell === 'A') {
          this.addPickup(row, col, world, 'ammo');
        } else if (DOOR_BY_CELL[cell]) {
          this.addDoor(row, col, world, DOOR_BY_CELL[cell]);
        } else if (cell === 'X') {
          this.addExit(world);
        } else if (cell === '?') {
          this.addSecret(row, col, world);
        } else if (cell === 'L') {
          this.addElevator(row, col, world);
        } else if (cell === 'T') {
          this.addTrap(world, row * width + col);
        }
        if (cell === '.' && (row * 11 + col * 5 + this.levelIndex) % 31 === 0) this.addPuddle(world, row * width + col);
        if (cell === '.' && (row * 7 + col * 17 + this.levelIndex * 13) % 47 === 0) {
          this.addFloorMarker(world, level.lightColors[(row + col) % level.lightColors.length], (row + col) % 2 === 0);
        }
        if (cell === '.' && (row * 19 + col * 13 + this.levelIndex * 7) % 41 === 0) {
          this.addBathroomProp(row, col, world);
        }
      }
    }
    const transform = new THREE.Matrix4();
    wallInstances.forEach((positions, materialIndex) => {
      if (positions.length === 0) return;
      const walls = new THREE.InstancedMesh(wallGeometry, wallMaterials[materialIndex], positions.length);
      positions.forEach((position, instanceIndex) => {
        transform.makeTranslation(position.x, position.y, position.z);
        walls.setMatrixAt(instanceIndex, transform);
      });
      walls.instanceMatrix.needsUpdate = true;
      walls.castShadow = true;
      walls.receiveShadow = true;
      this.scene.add(walls);
      this.worldRaycast.push(walls);
    });
    const scaledStaticCount = getScaledEnemyCount(duckCount, this.playerCount);
    const reinforcementCells = getReinforcementCells(level.grid, scaledStaticCount - duckCount, this.levelIndex * 101 + this.playerCount * 17);
    reinforcementCells.forEach((cell, index) => {
      const kind = getDuckKindForSpawn(duckCount + index, this.levelIndex);
      this.addDuck(
        `duck-${this.levelIndex}-reinforcement-${index}`,
        kind,
        this.cellToWorld(cell.row, cell.col, width, height),
      );
    });
    this.addWallPosters(level);
    this.scene.add(this.particles.points);
  }

  private addDuck(id: string, kind: DuckKind, position: THREE.Vector3): void {
    const difficulty = DIFFICULTIES[this.settings.difficulty] ?? DIFFICULTIES.normal;
    const duck = new Duck(id, kind, position, {
      health: difficulty.enemyHealth,
      damage: difficulty.enemyDamage,
      speed: difficulty.enemySpeed,
    });
    this.ducks.set(duck.id, duck);
    this.scene.add(duck.group);
    this.worldRaycast.push(duck.group);
  }

  private addPipe(x: number, z: number, horizontal: boolean): void {
    const group = new THREE.Group();
    const material = new THREE.MeshStandardMaterial({ color: 0xc4612b, emissive: 0x4b1609, emissiveIntensity: 0.16, metalness: 0.66, roughness: 0.38 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x20282a, metalness: 0.78, roughness: 0.34 });
    const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 2.4, 8), material);
    pipe.rotation.z = horizontal ? Math.PI / 2 : 0;
    const coupling = new THREE.Mesh(new THREE.TorusGeometry(0.17, 0.05, 5, 10), dark);
    coupling.rotation.x = horizontal ? Math.PI / 2 : 0;
    coupling.position.y = horizontal ? 0 : 0.72;
    coupling.position.x = horizontal ? 0.72 : 0;
    const valve = new THREE.Mesh(new THREE.TorusGeometry(0.25, 0.045, 5, 10), material);
    valve.position.set(horizontal ? 0 : 0.22, horizontal ? 0.22 : 0, 0.18);
    group.add(pipe, coupling, valve);
    group.position.set(x, 1.65, z);
    this.scene.add(group);
  }

  private addCeilingGrid(level: LevelDefinition, width: number, height: number): void {
    const spanX = width * CELL;
    const spanZ = height * CELL;
    const beamMaterial = new THREE.MeshStandardMaterial({ color: 0x1b2325, roughness: 0.42, metalness: 0.78 });
    const rustMaterial = new THREE.MeshStandardMaterial({ color: 0x9c4322, emissive: 0x451309, emissiveIntensity: 0.1, roughness: 0.4, metalness: 0.55 });

    for (let row = 2; row < height; row += 4) {
      const beam = new THREE.Mesh(new THREE.BoxGeometry(spanX, 0.14, 0.18), row % 8 === 2 ? rustMaterial : beamMaterial);
      beam.position.set(0, WALL_HEIGHT - 0.11, (row - (height - 1) / 2) * CELL);
      this.scene.add(beam);
    }
    for (let col = 3; col < width; col += 6) {
      const beam = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.14, spanZ), col % 12 === 3 ? rustMaterial : beamMaterial);
      beam.position.set((col - (width - 1) / 2) * CELL, WALL_HEIGHT - 0.1, 0);
      this.scene.add(beam);
    }

    const housingMaterial = new THREE.MeshStandardMaterial({ color: 0x263033, roughness: 0.36, metalness: 0.72 });
    const panelMaterials = level.lightColors.map((color) => new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 1.35,
      roughness: 0.26,
      metalness: 0.08,
    }));
    this.animatedFixtureMaterials.push(...panelMaterials);
    let fixtureIndex = 0;
    for (let row = 2; row < height - 1; row += 1) {
      for (let col = 2; col < width - 1; col += 1) {
        if (level.grid[row][col] === '#') continue;
        if ((row * 23 + col * 17 + this.levelIndex * 29) % 53 !== 0) continue;
        const world = this.cellToWorld(row, col, width, height);
        const housing = new THREE.Mesh(new THREE.BoxGeometry(2.1, 0.16, 0.72), housingMaterial);
        housing.position.set(world.x, WALL_HEIGHT - 0.19, world.z);
        housing.rotation.y = (row + col) % 2 === 0 ? 0 : Math.PI / 2;
        const panelMaterial = panelMaterials[fixtureIndex % panelMaterials.length];
        const panel = new THREE.Mesh(new THREE.BoxGeometry(1.62, 0.06, 0.34), panelMaterial);
        panel.position.copy(housing.position).setY(WALL_HEIGHT - 0.29);
        panel.rotation.y = housing.rotation.y;
        this.scene.add(housing, panel);
        fixtureIndex += 1;
      }
    }
  }

  private addFloorMarker(world: THREE.Vector3, color: number, vertical: boolean): void {
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.32,
      transparent: true,
      opacity: 0.72,
      roughness: 0.44,
      metalness: 0.12,
      depthWrite: false,
    });
    for (const offset of [-0.56, 0.56]) {
      const stripe = new THREE.Mesh(new THREE.PlaneGeometry(vertical ? 0.14 : 2.5, vertical ? 2.5 : 0.14), material);
      stripe.rotation.x = -Math.PI / 2;
      stripe.position.set(world.x + (vertical ? offset : 0), 0.022, world.z + (vertical ? 0 : offset));
      this.scene.add(stripe);
    }
  }

  private addWallTrim(world: THREE.Vector3, color: number): void {
    const material = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.12,
      metalness: 0.54,
      roughness: 0.4,
    });
    for (const y of [0.7, 2.72]) {
      const band = new THREE.Mesh(new THREE.BoxGeometry(CELL * 1.012, 0.16, CELL * 1.012), material);
      band.position.set(world.x, y, world.z);
      this.scene.add(band);
    }
  }

  private addBathroomProp(row: number, col: number, world: THREE.Vector3): void {
    if (!this.level) return;
    const group = new THREE.Group();
    const porcelain = new THREE.MeshStandardMaterial({ color: 0xd9eeee, roughness: 0.28, metalness: 0.02, flatShading: true });
    const dark = new THREE.MeshStandardMaterial({ color: 0x172125, roughness: 0.55, metalness: 0.58, flatShading: true });
    const accentColor = this.level.lightColors[(row + col) % this.level.lightColors.length];
    const accent = new THREE.MeshStandardMaterial({ color: accentColor, emissive: accentColor, emissiveIntensity: 0.15, roughness: 0.36, flatShading: true });
    const wallDirections = [
      { row: -1, col: 0, offset: [0, -1.25] as const, rotation: 0 },
      { row: 0, col: 1, offset: [1.25, 0] as const, rotation: -Math.PI / 2 },
      { row: 1, col: 0, offset: [0, 1.25] as const, rotation: Math.PI },
      { row: 0, col: -1, offset: [-1.25, 0] as const, rotation: Math.PI / 2 },
    ];
    const wall = wallDirections.find((direction) => this.level?.grid[row + direction.row]?.[col + direction.col] === '#');
    const type = Math.abs(row * 31 + col * 17 + this.levelIndex * 11) % 5;

    if (type === 0) {
      const bowl = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.14, 6, 10), porcelain);
      bowl.rotation.x = Math.PI / 2;
      bowl.position.set(0, 0.48, 0.18);
      const base = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.34, 0.5, 8), porcelain);
      base.position.y = 0.25;
      const tank = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.72, 0.32), porcelain);
      tank.position.set(0, 0.72, -0.38);
      const handle = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.08, 0.1), accent);
      handle.position.set(0.38, 0.78, -0.48);
      group.add(bowl, base, tank, handle);
    } else if (type === 1) {
      const machine = new THREE.Mesh(new THREE.BoxGeometry(1.3, 1.55, 0.88), dark);
      machine.position.y = 0.78;
      const rim = new THREE.Mesh(new THREE.TorusGeometry(0.38, 0.09, 6, 12), accent);
      rim.position.set(0, 0.74, 0.47);
      const glass = new THREE.Mesh(new THREE.CircleGeometry(0.3, 12), new THREE.MeshStandardMaterial({ color: 0x123c48, emissive: 0x197a8b, emissiveIntensity: 0.22, metalness: 0.55, roughness: 0.18 }));
      glass.position.set(0, 0.74, 0.525);
      const controls = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.18, 0.08), accent);
      controls.position.set(0, 1.28, 0.49);
      group.add(machine, rim, glass, controls);
    } else if (type === 2) {
      const pedestal = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.28, 0.92, 8), porcelain);
      pedestal.position.y = 0.46;
      const basin = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.34, 0.24, 10), porcelain);
      basin.position.y = 0.96;
      const tap = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.055, 5, 8, Math.PI), accent);
      tap.position.set(0, 1.18, -0.12);
      tap.rotation.z = Math.PI / 2;
      group.add(pedestal, basin, tap);
    } else if (type === 3) {
      const tub = new THREE.Group();
      for (const [x, z, width, depth] of [[0, -0.48, 1.55, 0.14], [0, 0.48, 1.55, 0.14], [-0.7, 0, 0.14, 1.05], [0.7, 0, 0.14, 1.05]] as const) {
        const side = new THREE.Mesh(new THREE.BoxGeometry(width, 0.55, depth), porcelain);
        side.position.set(x, 0.36, z);
        tub.add(side);
      }
      const water = new THREE.Mesh(new THREE.PlaneGeometry(1.25, 0.72), accent);
      water.rotation.x = -Math.PI / 2;
      water.position.y = 0.56;
      tub.add(water);
      group.add(tub);
      group.scale.setScalar(0.82);
    } else {
      const roll = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.28, 0.42, 10), porcelain);
      roll.rotation.z = Math.PI / 2;
      roll.position.set(-0.28, 0.3, 0);
      const plungerStick = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 1.1, 6), accent);
      plungerStick.position.set(0.35, 0.63, 0.05);
      plungerStick.rotation.z = -0.18;
      const plunger = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.32, 0.28, 8), new THREE.MeshStandardMaterial({ color: 0xc43f31, roughness: 0.68, flatShading: true }));
      plunger.position.set(0.45, 0.16, 0.05);
      group.add(roll, plungerStick, plunger);
    }

    if (wall) {
      group.position.set(world.x + wall.offset[0], 0, world.z + wall.offset[1]);
      group.rotation.y = wall.rotation;
    } else {
      group.position.set(world.x + 0.6, 0, world.z - 0.55);
      group.rotation.y = (row + col) * 0.7;
      group.scale.multiplyScalar(0.82);
    }
    group.traverse((node) => {
      if (node instanceof THREE.Mesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });
    this.scene.add(group);
  }

  private addWallPosters(level: LevelDefinition): void {
    const height = level.grid.length;
    const width = level.grid[0].length;
    const candidates: Array<{ row: number; col: number; normal: THREE.Vector3; rotation: number; score: number }> = [];
    const directions = [
      { row: -1, col: 0, normal: new THREE.Vector3(0, 0, -1), rotation: Math.PI },
      { row: 0, col: 1, normal: new THREE.Vector3(1, 0, 0), rotation: Math.PI / 2 },
      { row: 1, col: 0, normal: new THREE.Vector3(0, 0, 1), rotation: 0 },
      { row: 0, col: -1, normal: new THREE.Vector3(-1, 0, 0), rotation: -Math.PI / 2 },
    ];
    for (let row = 1; row < height - 1; row += 1) {
      for (let col = 1; col < width - 1; col += 1) {
        if (level.grid[row][col] !== '#') continue;
        const openSide = directions.find((direction) => level.grid[row + direction.row][col + direction.col] !== '#');
        if (!openSide) continue;
        candidates.push({
          row,
          col,
          normal: openSide.normal,
          rotation: openSide.rotation,
          score: ((row * 73856093) ^ (col * 19349663) ^ (this.levelIndex * 83492791)) >>> 0,
        });
      }
    }
    candidates.sort((a, b) => a.score - b.score);
    const texture = new THREE.TextureLoader().load('/assets/quack-hazard-poster.png');
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    const material = new THREE.MeshStandardMaterial({ map: texture, emissive: 0x15252a, emissiveIntensity: 0.26, roughness: 0.42, metalness: 0.12 });
    const signCopy = [
      'VIETATO\nDARE PANE',
      'QUACK =\nALLARME',
      'WC-13\nNON TIRARE',
      'CASCO E\nCIABATTE',
    ];
    for (const [index, candidate] of candidates.slice(0, 7).entries()) {
      const world = this.cellToWorld(candidate.row, candidate.col, width, height);
      const posterMaterial = index < 3
        ? material
        : new THREE.MeshStandardMaterial({
          map: this.createWarningTexture(signCopy[(index - 3) % signCopy.length], level.lightColors[index % level.lightColors.length]),
          emissive: level.wallAccent,
          emissiveIntensity: 0.2,
          roughness: 0.48,
          metalness: 0.08,
        });
      const poster = new THREE.Mesh(new THREE.PlaneGeometry(2.25, 2.25), posterMaterial);
      poster.position.copy(world).addScaledVector(candidate.normal, CELL / 2 + 0.015);
      poster.position.y = 1.88;
      poster.rotation.y = candidate.rotation;
      this.scene.add(poster);
    }
  }

  private addPuddle(world: THREE.Vector3, seed: number): void {
    const radius = 1.2 + (seed % 5) * 0.13;
    const puddle = new THREE.Mesh(
      new THREE.CircleGeometry(radius, 10),
      new THREE.MeshStandardMaterial({
        color: this.level?.puddleColor ?? 0x4ca8b8,
        emissive: this.level?.puddleColor ?? 0x4ca8b8,
        emissiveIntensity: 0.16,
        transparent: true,
        opacity: 0.44,
        roughness: 0.12,
        metalness: 0.08,
        depthWrite: false,
      }),
    );
    puddle.position.set(world.x + ((seed % 3) - 1) * 0.38, 0.018, world.z + (((seed >> 2) % 3) - 1) * 0.34);
    puddle.rotation.x = -Math.PI / 2;
    this.scene.add(puddle);
    const bubbles: THREE.Mesh[] = [];
    const bubbleMaterial = new THREE.MeshStandardMaterial({ color: 0xdafcff, emissive: 0x49b9ca, emissiveIntensity: 0.35, roughness: 0.24 });
    for (let index = 0; index < 3; index += 1) {
      const bubble = new THREE.Mesh(new THREE.IcosahedronGeometry(0.08 + index * 0.025, 1), bubbleMaterial);
      const angle = seed * 0.17 + index * 2.1;
      bubble.position.set(
        puddle.position.x + Math.cos(angle) * radius * 0.52,
        0.08 + index * 0.03,
        puddle.position.z + Math.sin(angle) * radius * 0.52,
      );
      this.scene.add(bubble);
      bubbles.push(bubble);
    }
    this.puddles.push({
      position: new THREE.Vector2(puddle.position.x, puddle.position.z),
      radius,
      mesh: puddle,
      bubbles,
      phase: seed * 0.31,
    });
  }

  private addSecret(row: number, col: number, world: THREE.Vector3): void {
    const id = `secret-${this.levelIndex}-${row}-${col}`;
    const vertical = this.level?.grid[row - 1]?.[col] === '#' || this.level?.grid[row + 1]?.[col] === '#';
    const wall = new THREE.Mesh(
      new THREE.BoxGeometry(vertical ? 0.68 : CELL, WALL_HEIGHT, vertical ? CELL : 0.68),
      new THREE.MeshStandardMaterial({
        map: this.createIndustrialTexture(this.level?.wallAccent ?? 0x485456, true),
        color: 0x788081,
        emissive: this.level?.wallAccent ?? 0x485456,
        emissiveIntensity: 0.06,
        roughness: 0.72,
        metalness: 0.38,
      }),
    );
    wall.position.set(world.x, WALL_HEIGHT / 2, world.z);
    wall.castShadow = true;
    wall.receiveShadow = true;
    wall.userData.secretId = id;
    const valve = new THREE.Mesh(
      new THREE.TorusGeometry(0.28, 0.055, 5, 10),
      new THREE.MeshStandardMaterial({ color: 0x8b4a2d, metalness: 0.62, roughness: 0.4 }),
    );
    valve.position.set(vertical ? 0.37 : 0, 0.18, vertical ? 0 : 0.37);
    valve.rotation.y = vertical ? Math.PI / 2 : 0;
    wall.add(valve);
    this.scene.add(wall);
    this.worldRaycast.push(wall);
    this.secrets.set(id, { id, row, col, mesh: wall, open: false, progress: 0 });
  }

  private addElevator(row: number, col: number, world: THREE.Vector3): void {
    const id = `elevator-${this.levelIndex}-${row}-${col}`;
    const group = new THREE.Group();
    const dark = new THREE.MeshStandardMaterial({ color: 0x182326, metalness: 0.76, roughness: 0.36, flatShading: true });
    const signal = new THREE.MeshStandardMaterial({
      color: 0x58c9d4,
      emissive: 0x2e9eaa,
      emissiveIntensity: 0.7,
      metalness: 0.22,
      roughness: 0.28,
      flatShading: true,
    });
    const platform = new THREE.Mesh(new THREE.CylinderGeometry(1.18, 1.28, 0.16, 12), dark);
    platform.position.y = 0.08;
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.86, 0.08, 5, 12), signal);
    ring.position.y = 0.17;
    ring.rotation.x = Math.PI / 2;
    const arrows = new THREE.Group();
    for (let index = 0; index < 3; index += 1) {
      const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.3, 4), signal);
      arrow.position.set((index - 1) * 0.34, 0.38, 0);
      arrow.rotation.z = Math.PI;
      arrows.add(arrow);
    }
    group.add(platform, ring, arrows);
    group.position.set(world.x, 0, world.z);
    this.scene.add(group);
    this.elevators.push({ id, position: new THREE.Vector3(world.x, EYE_HEIGHT, world.z), mesh: group });
  }

  private addTrap(world: THREE.Vector3, seed: number): void {
    const group = new THREE.Group();
    const dark = new THREE.MeshStandardMaterial({ color: 0x20282a, metalness: 0.75, roughness: 0.38 });
    const signal = new THREE.MeshStandardMaterial({
      color: 0xe46628,
      emissive: 0x8d220c,
      emissiveIntensity: 0.3,
      metalness: 0.3,
      roughness: 0.34,
    });
    const grate = new THREE.Mesh(new THREE.CylinderGeometry(0.72, 0.72, 0.08, 12), dark);
    grate.position.y = 0.04;
    for (let index = 0; index < 4; index += 1) {
      const slit = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.04, 0.86), signal);
      slit.position.set((index - 1.5) * 0.22, 0.1, 0);
      group.add(slit);
    }
    group.add(grate);
    group.position.set(world.x, 0, world.z);
    this.scene.add(group);
    this.traps.push({ position: new THREE.Vector2(world.x, world.z), mesh: group, signal, phase: (seed % 7) * 0.37 });
  }

  private addDoor(row: number, col: number, world: THREE.Vector3, color: KeyColor): void {
    const colors: Record<KeyColor, number> = { yellow: 0xd7a72c, cyan: 0x3fbec9, red: 0xc84b38 };
    const verticalBarrier = this.level?.grid[row - 1]?.[col] === '#' && this.level?.grid[row + 1]?.[col] === '#';
    const darkFrame = new THREE.MeshStandardMaterial({ color: 0x171e20, roughness: 0.38, metalness: 0.78, flatShading: true });
    const signal = new THREE.MeshStandardMaterial({
      color: colors[color],
      emissive: colors[color],
      emissiveIntensity: 0.55,
      roughness: 0.3,
      metalness: 0.35,
      flatShading: true,
    });
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(verticalBarrier ? 0.52 : CELL * 0.92, WALL_HEIGHT * 0.94, verticalBarrier ? CELL * 0.92 : 0.52),
      new THREE.MeshStandardMaterial({ color: colors[color], metalness: 0.62, roughness: 0.36 }),
    );
    mesh.position.set(world.x, WALL_HEIGHT / 2, world.z);
    mesh.castShadow = true;
    const frame = new THREE.Group();
    const sideGeometry = new THREE.BoxGeometry(verticalBarrier ? 0.68 : 0.34, WALL_HEIGHT, verticalBarrier ? 0.34 : 0.68);
    for (const side of [-1, 1]) {
      const pillar = new THREE.Mesh(sideGeometry, darkFrame);
      pillar.position.set(verticalBarrier ? 0 : side * 1.9, WALL_HEIGHT / 2, verticalBarrier ? side * 1.9 : 0);
      pillar.castShadow = true;
      const lamp = new THREE.Mesh(new THREE.BoxGeometry(verticalBarrier ? 0.74 : 0.16, 0.24, verticalBarrier ? 0.16 : 0.74), signal);
      lamp.position.set(verticalBarrier ? -0.38 : side * 1.9, 2.55, verticalBarrier ? side * 1.9 : -0.38);
      frame.add(pillar, lamp);
    }
    const header = new THREE.Mesh(
      new THREE.BoxGeometry(verticalBarrier ? 0.72 : CELL, 0.34, verticalBarrier ? CELL : 0.72),
      darkFrame,
    );
    header.position.y = WALL_HEIGHT - 0.2;
    frame.add(header);
    for (let stripeIndex = -2; stripeIndex <= 2; stripeIndex += 1) {
      const stripe = new THREE.Mesh(
        new THREE.BoxGeometry(verticalBarrier ? 0.78 : 0.38, 0.1, verticalBarrier ? 0.38 : 0.78),
        signal,
      );
      stripe.position.set(
        verticalBarrier ? -0.4 : stripeIndex * 0.62,
        WALL_HEIGHT - 0.19,
        verticalBarrier ? stripeIndex * 0.62 : -0.4,
      );
      stripe.rotation.y = verticalBarrier ? 0 : Math.PI / 2;
      frame.add(stripe);
    }
    frame.position.copy(world);
    this.scene.add(frame);
    this.addFloorMarker(world, colors[color], verticalBarrier);
    const id = `door-${this.levelIndex}-${row}-${col}`;
    mesh.userData.doorId = id;
    this.scene.add(mesh);
    this.worldRaycast.push(mesh);
    this.doors.set(id, { id, row, col, color, mesh, open: false, progress: 0 });
  }

  private addPickup(row: number, col: number, world: THREE.Vector3, kind: 'key' | 'health' | 'ammo', key?: KeyColor): void {
    const group = new THREE.Group();
    const id = `pickup-${this.levelIndex}-${row}-${col}`;
    if (kind === 'key' && key) {
      const colors: Record<KeyColor, number> = { yellow: 0xf0c82f, cyan: 0x5bd6dd, red: 0xdb5743 };
      const material = new THREE.MeshStandardMaterial({ color: colors[key], emissive: colors[key], emissiveIntensity: 0.28, metalness: 0.45 });
      const shaft = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.22, 1.15), material);
      shaft.rotation.y = Math.PI / 2;
      const ring = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.1, 6, 10), material);
      ring.position.x = -0.7;
      ring.rotation.y = Math.PI / 2;
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.24, 0.28), material);
      tooth.position.x = 0.48;
      tooth.position.z = 0.18;
      group.add(shaft, ring, tooth);
    } else if (kind === 'health') {
      const soap = new THREE.Mesh(
        new THREE.BoxGeometry(1.15, 0.36, 0.72),
        new THREE.MeshStandardMaterial({ color: 0xe9eee2, emissive: 0x6bc8d0, emissiveIntensity: 0.18, roughness: 0.38 }),
      );
      soap.rotation.y = 0.3;
      group.add(soap);
    } else {
      const crateMaterial = new THREE.MeshStandardMaterial({ color: 0x283438, roughness: 0.48, metalness: 0.62, flatShading: true });
      const foamMaterial = new THREE.MeshStandardMaterial({ color: 0xe98135, emissive: 0xa23b19, emissiveIntensity: 0.25, roughness: 0.42 });
      const crate = new THREE.Mesh(new THREE.BoxGeometry(1.05, 0.72, 0.82), crateMaterial);
      const band = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.77, 0.87), foamMaterial);
      const canister = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.13, 0.72, 8), foamMaterial);
      canister.rotation.z = Math.PI / 2;
      canister.position.set(0, 0.48, 0);
      group.add(crate, band, canister);
    }
    group.position.set(world.x, 0.85, world.z);
    group.userData.pickupId = id;
    this.scene.add(group);
    this.pickups.set(id, { id, row, col, kind, key, mesh: group, active: true });
  }

  private addExit(world: THREE.Vector3): void {
    const group = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.25, 0.22, 8, 16),
      new THREE.MeshStandardMaterial({ color: 0x71d6d0, emissive: 0x2b9596, emissiveIntensity: 0.9, metalness: 0.4 }),
    );
    ring.rotation.x = Math.PI / 2;
    const drain = new THREE.Mesh(
      new THREE.CylinderGeometry(1.05, 1.05, 0.1, 16),
      new THREE.MeshStandardMaterial({ color: 0x1f3d40, emissive: 0x1b6c70, emissiveIntensity: 0.4, metalness: 0.75 }),
    );
    group.add(ring, drain);
    group.position.set(world.x, 0.14, world.z);
    this.scene.add(group);
    this.exit = group;
  }

  private buildWeapon(): void {
    this.weaponRig.clear();
    const weapon = this.weapons[this.activeWeapon];
    const bodyMaterial = new THREE.MeshStandardMaterial({ color: weapon.color, emissive: weapon.color, emissiveIntensity: 0.16, roughness: 0.3, metalness: 0.32, flatShading: true });
    const dark = new THREE.MeshStandardMaterial({ color: 0x182326, roughness: 0.4, metalness: 0.72, flatShading: true });
    const black = new THREE.MeshStandardMaterial({ color: 0x090d0e, roughness: 0.56, metalness: 0.5, flatShading: true });
    const orange = new THREE.MeshStandardMaterial({ color: 0xe46628, emissive: 0x8b2b0e, emissiveIntensity: 0.25, roughness: 0.32, metalness: 0.46, flatShading: true });
    const soap = new THREE.MeshStandardMaterial({ color: 0x9df4ef, emissive: 0x2d9ead, emissiveIntensity: 0.34, transparent: true, opacity: 0.76, roughness: 0.14, metalness: 0.05, flatShading: true });
    const paper = new THREE.MeshStandardMaterial({ color: 0xe8f1ed, emissive: 0x6ab5ba, emissiveIntensity: 0.08, roughness: 0.82, metalness: 0.01, flatShading: true });

    const grip = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.45, 0.24), dark);
    grip.position.set(0, -0.26, 0.12);
    grip.rotation.x = -0.28;
    const trigger = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.025, 5, 8, Math.PI), orange);
    trigger.position.set(0, -0.12, -0.08);
    trigger.rotation.x = Math.PI / 2;
    const gauge = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.07, 8), orange);
    gauge.position.set(0.28, 0.18, -0.06);
    gauge.rotation.z = Math.PI / 2;
    const sight = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.08, 0.16), orange);
    sight.position.set(0, 0.31, -0.38);
    this.weaponRig.add(grip, trigger, gauge, sight);

    if (this.activeWeapon === 0) {
      const body = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.4, 0.74), bodyMaterial);
      body.position.z = -0.08;
      const pressureTank = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.18, 0.5, 8), dark);
      pressureTank.position.set(0, 0.3, 0.05);
      pressureTank.rotation.z = Math.PI / 2;
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.14, 0.86, 8), dark);
      barrel.rotation.x = Math.PI / 2;
      barrel.position.z = -0.76;
      const pump = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.15, 0.5), orange);
      pump.position.set(0, -0.08, -0.58);
      const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.29, 0.24, 10), new THREE.MeshStandardMaterial({ color: 0xb9362f, roughness: 0.72, flatShading: true }));
      cup.rotation.x = Math.PI / 2;
      cup.position.z = -1.2;
      this.weaponRig.add(body, pressureTank, barrel, pump, cup);
    } else if (this.activeWeapon === 1) {
      const chassis = new THREE.Mesh(new THREE.DodecahedronGeometry(0.29, 0), bodyMaterial);
      chassis.position.z = -0.08;
      chassis.scale.set(1.05, 0.78, 1.35);
      const tank = new THREE.Mesh(new THREE.IcosahedronGeometry(0.32, 1), soap);
      tank.position.set(0, 0.18, -0.08);
      const cage = new THREE.Mesh(new THREE.TorusGeometry(0.35, 0.045, 5, 12), orange);
      cage.position.copy(tank.position);
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.19, 0.58, 8), dark);
      barrel.rotation.x = Math.PI / 2;
      barrel.position.z = -0.62;
      const muzzle = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.055, 5, 12), bodyMaterial);
      muzzle.position.z = -0.93;
      for (let index = 0; index < 3; index += 1) {
        const bubble = new THREE.Mesh(new THREE.IcosahedronGeometry(0.07 + index * 0.018, 1), soap);
        bubble.position.set((index - 1) * 0.12, 0.05 + index * 0.08, -0.98 - index * 0.09);
        this.weaponRig.add(bubble);
      }
      this.weaponRig.add(chassis, tank, cage, barrel, muzzle);
    } else if (this.activeWeapon === 2) {
      const chassis = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.4, 0.74), bodyMaterial);
      chassis.position.z = -0.08;
      const top = new THREE.Mesh(new THREE.BoxGeometry(0.48, 0.11, 0.56), dark);
      top.position.set(0, 0.25, -0.08);
      const front = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.25, 0.42, 12), black);
      front.position.z = -0.55;
      front.rotation.x = Math.PI / 2;
      const muzzle = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.13, 0.1), orange);
      muzzle.position.z = -0.79;
      for (const side of [-1, 1]) {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(0.09, 0.24, 0.88), orange);
        rail.position.set(side * 0.34, 0.02, -0.17);
        const roll = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.09, 6, 10), paper);
        roll.position.set(side * 0.16, 0.37, -0.08 + side * 0.06);
        roll.rotation.y = Math.PI / 2;
        const sheet = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.28, 0.025), paper);
        sheet.position.set(side * 0.16, 0.22, -0.09 + side * 0.06);
        this.weaponRig.add(rail, roll, sheet);
      }
      this.weaponRig.add(chassis, top, front, muzzle);
    } else {
      const manifold = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.34, 0.52, 10), bodyMaterial);
      manifold.rotation.x = Math.PI / 2;
      manifold.position.z = -0.2;
      const hose = new THREE.Mesh(new THREE.TorusGeometry(0.31, 0.07, 6, 12, Math.PI * 1.4), dark);
      hose.position.set(-0.22, -0.05, 0.02);
      hose.rotation.z = -0.5;
      const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, 0.72, 8), dark);
      neck.rotation.x = Math.PI / 2;
      neck.position.z = -0.72;
      const showerHead = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.25, 0.13, 12), bodyMaterial);
      showerHead.rotation.x = Math.PI / 2;
      showerHead.position.z = -1.12;
      for (let index = 0; index < 5; index += 1) {
        const nozzle = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.09, 5), soap);
        const angle = (index / 5) * Math.PI * 2;
        nozzle.position.set(Math.cos(angle) * 0.17, Math.sin(angle) * 0.17, -1.23);
        nozzle.rotation.x = Math.PI / 2;
        this.weaponRig.add(nozzle);
      }
      this.weaponRig.add(manifold, hose, neck, showerHead);
    }
    this.weaponRig.scale.setScalar(0.68);
    this.weaponRig.position.set(0.48, -0.47, -0.96);
    this.weaponRig.rotation.set(0, -0.12, 0);
  }

  private createIndustrialTexture(color: number, rivets: boolean): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 96;
    canvas.height = 96;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D non disponibile');
    const base = new THREE.Color(color);
    ctx.fillStyle = `#${base.getHexString()}`;
    ctx.fillRect(0, 0, 96, 96);
    ctx.fillStyle = 'rgba(255,255,255,.075)';
    for (let i = 0; i < 126; i += 1) ctx.fillRect((i * 17) % 96, (i * 29) % 96, i % 5 === 0 ? 3 : 2, 1);
    ctx.fillStyle = 'rgba(0,0,0,.12)';
    for (let i = 0; i < 44; i += 1) ctx.fillRect((i * 41) % 96, (i * 23) % 96, 4, 3);
    ctx.strokeStyle = 'rgba(0,0,0,.42)';
    ctx.lineWidth = 3;
    ctx.strokeRect(2, 2, 92, 92);
    ctx.beginPath();
    ctx.moveTo(0, 48);
    ctx.lineTo(96, 48);
    ctx.moveTo(48, 0);
    ctx.lineTo(48, 96);
    ctx.stroke();
    if (rivets) {
      ctx.fillStyle = 'rgba(230,220,190,.55)';
      for (const [x, y] of [[7, 7], [89, 7], [7, 89], [89, 89]]) ctx.fillRect(x - 2, y - 2, 5, 5);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestMipmapNearestFilter;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    return texture;
  }

  private createWarningTexture(copy: string, accent: number): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = 192;
    canvas.height = 192;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D non disponibile');
    ctx.fillStyle = '#12191b';
    ctx.fillRect(0, 0, 192, 192);
    const accentColor = new THREE.Color(accent);
    ctx.fillStyle = `#${accentColor.getHexString()}`;
    ctx.fillRect(10, 10, 172, 20);
    ctx.fillRect(10, 162, 172, 20);
    ctx.strokeStyle = '#d9eeee';
    ctx.lineWidth = 5;
    ctx.strokeRect(10, 10, 172, 172);
    ctx.fillStyle = '#edf7f3';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = '900 29px monospace';
    const lines = copy.split('\n');
    lines.forEach((line, index) => ctx.fillText(line, 96, 82 + index * 36));
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestMipmapNearestFilter;
    return texture;
  }

  private frame(timestamp: number): void {
    this.timer.update(timestamp);
    const rawDt = Math.min(this.timer.getDelta(), 0.05);
    const now = performance.now() / 1000;
    this.elapsed += rawDt;
    if (this.status === 'playing' && !this.paused && document.visibilityState === 'visible') {
      this.smoothedFrameTime = THREE.MathUtils.lerp(this.smoothedFrameTime, rawDt, 0.035);
      if (now >= this.nextQualityCheckAt) {
        this.nextQualityCheckAt = now + 3;
        if (this.smoothedFrameTime > 1 / 44 && this.renderScale > 0.62) {
          this.renderScale = Math.max(0.62, this.renderScale - 0.08);
          this.resize();
        }
      }
    }
    if (this.status === 'playing' && (!this.paused || this.online)) {
      this.toastCooldown = Math.max(0, this.toastCooldown - rawDt);
      this.updatePlayer(rawDt);
      this.updateDowned(now);
      this.updateDoors(rawDt);
      this.updatePickups(now);
      this.updateDucks(now, rawDt);
      this.updateHorde(now);
      this.updatePuddles(now, rawDt);
      this.updateTraps(now, rawDt);
      this.updateAtmosphere(now);
      this.updateReload(now);
      this.updateExit(now);
      this.updateWeapon(now, rawDt);
      this.updateNetwork(now);
      this.particles.update(rawDt);
      if (now >= this.nextHudRefreshAt) {
        this.nextHudRefreshAt = now + 0.1;
        this.updateHud();
      }
      if (now >= this.nextMusicRefreshAt) {
        this.nextMusicRefreshAt = now + 0.3;
        this.updateMusicIntensity();
      }
    }
    this.renderer.render(this.scene, this.camera);
  }

  private updatePlayer(dt: number): void {
    if (document.pointerLockElement !== this.canvas || this.health <= 0) return;
    const forwardInput = Number(this.keysDown.has('KeyW')) - Number(this.keysDown.has('KeyS'));
    const sideInput = Number(this.keysDown.has('KeyD')) - Number(this.keysDown.has('KeyA'));
    const yaw = this.camera.rotation.y;
    const forward = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
    const right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
    const wish = forward.multiplyScalar(forwardInput).add(right.multiplyScalar(sideInput));
    if (wish.lengthSq() > 1) wish.normalize();
    const moving = wish.lengthSq() > 0.01;
    this.slowedByPuddle = this.isPlayerInPuddle();
    const wantsToSprint = (this.keysDown.has('ShiftLeft') || this.keysDown.has('ShiftRight')) && moving;
    this.sprinting = wantsToSprint && this.stamina > 0.5;
    if (this.sprinting) {
      const previousStamina = this.stamina;
      this.stamina = Math.max(0, this.stamina - SPRINT_DRAIN * dt);
      this.staminaRechargeDelay = 0.75;
      if (previousStamina > 0.5 && this.stamina <= 0.5) this.audio.sfx('sprint');
    } else {
      this.staminaRechargeDelay = Math.max(0, this.staminaRechargeDelay - dt);
      if (this.staminaRechargeDelay <= 0) this.stamina = Math.min(MAX_STAMINA, this.stamina + SPRINT_RECHARGE * dt);
    }
    const sprintMultiplier = this.sprinting ? 1.65 : 1;
    const puddleMultiplier = this.slowedByPuddle ? 0.56 : 1;
    const target = wish.multiplyScalar(5.65 * sprintMultiplier * puddleMultiplier);
    this.velocity.x = THREE.MathUtils.damp(this.velocity.x, target.x, 12, dt);
    this.velocity.z = THREE.MathUtils.damp(this.velocity.z, target.z, 12, dt);
    const nextX = this.camera.position.clone();
    nextX.x += this.velocity.x * dt;
    if (!this.isBlocked(nextX, PLAYER_RADIUS)) this.camera.position.x = nextX.x;
    const nextZ = this.camera.position.clone();
    nextZ.z += this.velocity.z * dt;
    if (!this.isBlocked(nextZ, PLAYER_RADIUS)) this.camera.position.z = nextZ.z;
    this.camera.position.y = EYE_HEIGHT;
    this.revealMinimap(this.camera.position, 5);

    const now = performance.now() / 1000;
    if (moving && this.slowedByPuddle && now >= this.nextFootSplashAt) {
      this.nextFootSplashAt = now + 0.18;
      const splashPosition = this.camera.position.clone().setY(0.12);
      this.particles.emit(splashPosition, this.level?.puddleColor ?? 0x5bd6dd, 5, { speed: 1.3, lift: 1.2, life: 0.48 });
      this.audio.sfx('splash');
    }
  }

  private updateDoors(dt: number): void {
    for (const door of this.doors.values()) {
      if (!door.open || door.progress >= 1) continue;
      door.progress = Math.min(1, door.progress + dt * 0.78);
      door.mesh.position.y = WALL_HEIGHT / 2 + door.progress * (WALL_HEIGHT + 0.4);
    }
    for (const secret of this.secrets.values()) {
      if (!secret.open || secret.progress >= 1) continue;
      secret.progress = Math.min(1, secret.progress + dt * 0.82);
      secret.mesh.position.y = WALL_HEIGHT / 2 + secret.progress * (WALL_HEIGHT + 0.4);
      if (secret.progress >= 1) secret.mesh.visible = false;
    }
    this.elevatorCooldown = Math.max(0, this.elevatorCooldown - dt);
  }

  private updateDowned(now: number): void {
    if (!this.online || this.health > 0 || this.reviveDeadline <= 0 || now < this.reviveDeadline) return;
    this.health = 100;
    this.reviveDeadline = 0;
    this.camera.position.copy(this.spawn);
    this.velocity.set(0, 0, 0);
    this.network?.send({ type: 'game-event', event: { kind: 'respawn', playerId: this.network.id } });
    this.callbacks.onToast('La squadra di emergenza ti ha rimesso in vasca.');
    this.updateHud();
    this.requestPointerLock();
  }

  private updatePuddles(time: number, dt: number): void {
    for (const puddle of this.puddles) {
      puddle.mesh.rotation.z += dt * 0.035;
      const material = puddle.mesh.material as THREE.MeshStandardMaterial;
      material.opacity = 0.38 + Math.sin(time * 1.8 + puddle.phase) * 0.07;
      puddle.bubbles.forEach((bubble, index) => {
        bubble.position.y = 0.09 + index * 0.035 + Math.sin(time * 2.7 + puddle.phase + index) * 0.045;
        const scale = 0.82 + Math.sin(time * 3.1 + index) * 0.18;
        bubble.scale.setScalar(scale);
      });
    }
  }

  private isPlayerInPuddle(): boolean {
    return this.puddles.some((puddle) => {
      const x = this.camera.position.x - puddle.position.x;
      const z = this.camera.position.z - puddle.position.y;
      return x * x + z * z <= puddle.radius * puddle.radius;
    });
  }

  private updateTraps(time: number, dt: number): void {
    this.trapDamageCooldown = Math.max(0, this.trapDamageCooldown - dt);
    for (const trap of this.traps) {
      const active = (time + trap.phase) % 3.2 < 0.64;
      trap.signal.emissiveIntensity = active ? 1.8 : 0.22;
      trap.mesh.scale.y = active ? 1.12 : 1;
      if (!active || this.trapDamageCooldown > 0 || this.paused) continue;
      const dx = this.camera.position.x - trap.position.x;
      const dz = this.camera.position.z - trap.position.y;
      const distanceSq = dx * dx + dz * dz;
      if (distanceSq > 1.45 * 1.45) continue;
      this.trapDamageCooldown = 0.85;
      this.applyDamage(
        this.settings.difficulty === 'nightmare' ? 16 : this.settings.difficulty === 'rubber' ? 7 : 11,
        new THREE.Vector3(trap.position.x, 0, trap.position.y),
      );
      this.audio.sfx('splash');
      const distance = Math.max(0.1, Math.sqrt(distanceSq));
      const pushed = this.camera.position.clone();
      pushed.x += (dx / distance) * 1.1;
      pushed.z += (dz / distance) * 1.1;
      if (!this.isBlocked(pushed, PLAYER_RADIUS)) this.camera.position.copy(pushed).setY(EYE_HEIGHT);
      this.particles.emit(new THREE.Vector3(trap.position.x, 0.12, trap.position.y), 0xff7838, 18, { speed: 3.8, lift: 4.6, gravity: 3.2, life: 0.72 });
      this.callbacks.onToast('GETTO DI VAPORE! Via dalla griglia!');
    }
  }

  private updateAtmosphere(time: number): void {
    this.animatedFixtureMaterials.forEach((fixture, index) => {
      const slowPulse = 1.08 + Math.sin(time * (1.7 + index * 0.23) + index * 2.1) * 0.22;
      const fault = (Math.floor(time * 3.2 + index * 7) % 41 === 0) ? 0.28 : 1;
      fixture.emissiveIntensity = slowPulse * fault;
    });
    if (this.traps.length === 0 || time < this.nextAmbientSteamAt) return;
    this.nextAmbientSteamAt = time + 0.24;
    const trap = this.traps[Math.floor(time * 4) % this.traps.length];
    const active = (time + trap.phase) % 3.2 < 0.64;
    if (!active) return;
    this.particles.emit(
      new THREE.Vector3(trap.position.x, 0.12, trap.position.y),
      0xc9f4ee,
      3,
      { speed: 0.75, lift: 3.8, gravity: -0.15, life: 1.25 },
    );
  }

  private updateReload(now: number): void {
    if (this.reloadingWeapon < 0 || now < this.reloadEndsAt) return;
    const weapon = this.weapons[this.reloadingWeapon];
    const needed = weapon.magazineSize - weapon.magazine;
    const loaded = Math.min(needed, weapon.reserve);
    weapon.magazine += loaded;
    weapon.reserve -= loaded;
    this.reloadingWeapon = -1;
    this.reloadEndsAt = 0;
    this.audio.sfx('reload');
    this.updateHud();
  }

  private updateMusicIntensity(): void {
    const alive = [...this.ducks.values()].filter((duck) => duck.alive);
    let nearest = Number.POSITIVE_INFINITY;
    for (const duck of alive) nearest = Math.min(nearest, duck.group.position.distanceTo(this.camera.position));
    const proximity = Number.isFinite(nearest) ? THREE.MathUtils.clamp(1 - (nearest - 4) / 22, 0, 1) : 0;
    const pressure = THREE.MathUtils.clamp(alive.length / 32, 0, 1) * 0.24;
    const bossPressure = alive.some((duck) => duck.kind === 'boss') ? 0.18 : 0;
    this.audio.setMusicState(this.levelIndex, Math.min(1, proximity * 0.72 + pressure + bossPressure));
  }

  private updatePickups(time: number): void {
    for (const pickup of this.pickups.values()) {
      if (!pickup.active) continue;
      pickup.mesh.rotation.y = time * 1.45;
      pickup.mesh.position.y = 0.82 + Math.sin(time * 3 + pickup.mesh.position.x) * 0.1;
      if (pickup.mesh.position.distanceTo(this.camera.position) > 1.35) continue;
      if (pickup.kind === 'health' && this.health >= 100) continue;
      if (pickup.kind === 'ammo' && this.weapons.every((weapon) => weapon.reserve >= weapon.maxReserve)) continue;
      this.collectPickup(pickup, true);
    }
  }

  private collectPickup(pickup: PickupState, announce: boolean): void {
    if (!pickup.active) return;
    pickup.active = false;
    pickup.mesh.visible = false;
    if (pickup.kind === 'key' && pickup.key) {
      this.keysOwned.add(pickup.key);
      const ambushCount = this.spawnKeyAmbush(pickup);
      this.callbacks.onToast(
        `Chiave ${this.keyLabel(pickup.key)} recuperata! IMBOSCATA: ${ambushCount} paperelle!`,
      );
      this.audio.sfx('quack');
    } else if (pickup.kind === 'health' && announce) {
      this.health = Math.min(100, this.health + 32);
      this.callbacks.onToast('Saponetta medica: +32 salute');
    } else if (pickup.kind === 'ammo' && announce) {
      const refills = [12, 16, 4, 72];
      this.weapons.forEach((weapon, index) => {
        weapon.reserve = Math.min(weapon.maxReserve, weapon.reserve + refills[index]);
      });
      this.callbacks.onToast('Cassetta da bagno: ventose, bolle, rotoli e acqua ricaricati!');
    }
    const particleColor = pickup.kind === 'ammo' ? 0xf07a35 : pickup.kind === 'key' ? 0xf5c935 : 0x77dfea;
    this.particles.emit(pickup.mesh.position.clone().setY(0.85), particleColor, 12, { speed: 2, lift: 2.8, life: 0.75 });
    this.audio.sfx('pickup');
    if (announce && this.online) {
      this.network?.send({ type: 'game-event', event: { kind: 'pickup', id: pickup.id, key: pickup.key } });
    }
    this.updateHud();
  }

  private spawnKeyAmbush(pickup: PickupState): number {
    if (!this.level || pickup.kind !== 'key') return 0;

    const firstId = `${pickup.id}-ambush-0`;
    if (this.ducks.has(firstId)) return 0;

    const height = this.level.grid.length;
    const width = this.level.grid[0].length;
    const ambushSize = getScaledEnemyCount(this.level.ambushSize, this.playerCount);
    const candidates = getKeyAmbushCells(
      this.level.grid,
      pickup.row,
      pickup.col,
      ambushSize,
    );

    let spawned = 0;
    for (const candidate of candidates) {
      const id = `${pickup.id}-ambush-${spawned}`;
      const kind = getDuckKindForSpawn(spawned + pickup.row + pickup.col, this.levelIndex);
      const position = this.cellToWorld(candidate.row, candidate.col, width, height);
      this.addDuck(id, kind, position);
      spawned += 1;
    }
    return spawned;
  }

  private updateDucks(time: number, dt: number): void {
    const authoritative = !this.online || this.network?.isHost;
    const targets = this.getTargets();
    this.duckSpatialBuckets.clear();
    if (authoritative) {
      for (const duck of this.ducks.values()) {
        if (!duck.alive) continue;
        const bucketX = Math.floor(duck.group.position.x / DUCK_BUCKET_SIZE);
        const bucketZ = Math.floor(duck.group.position.z / DUCK_BUCKET_SIZE);
        const key = duckBucketKey(bucketX, bucketZ);
        const bucket = this.duckSpatialBuckets.get(key);
        if (bucket) bucket.push(duck);
        else this.duckSpatialBuckets.set(key, [duck]);
      }
    }
    if (authoritative && targets.length > 0 && time >= this.nextNavRefreshAt) {
      this.nextNavRefreshAt = time + 0.28;
      this.refreshNavigationField(targets);
    }
    for (const duck of this.ducks.values()) {
      if (duck.alive) {
        const renderDistanceSq = duck.group.position.distanceToSquared(this.camera.position);
        duck.group.visible = renderDistanceSq <= DUCK_RENDER_DISTANCE_SQ;
        duck.setDetailVisible(renderDistanceSq <= DUCK_DETAIL_DISTANCE_SQ);
      }
      duck.updateVisual(time, dt);
      if (!authoritative || !duck.alive || targets.length === 0) continue;
      let nearest = targets[0];
      let nearestDistance = duck.group.position.distanceTo(nearest.position);
      for (let targetIndex = 1; targetIndex < targets.length; targetIndex += 1) {
        const target = targets[targetIndex];
        const distance = duck.group.position.distanceTo(target.position);
        if (distance < nearestDistance) {
          nearest = target;
          nearestDistance = distance;
        }
      }
      const pathWaypoint = this.getDuckWaypoint(duck);
      const waypoint = pathWaypoint ?? nearest.position;
      const pathDirection = this.duckPathDirection.subVectors(waypoint, duck.group.position).setY(0);
      const pathLengthSq = pathDirection.lengthSq();
      const lateral = this.duckLateral.set(0, 0, 0);
      if (pathLengthSq > 0.001) {
        const lateralScale = duck.laneOffset / Math.sqrt(pathLengthSq);
        lateral.set(-pathDirection.z * lateralScale, 0, pathDirection.x * lateralScale);
      }
      const separation = this.duckSeparation.set(0, 0, 0);
      const bucketX = Math.floor(duck.group.position.x / DUCK_BUCKET_SIZE);
      const bucketZ = Math.floor(duck.group.position.z / DUCK_BUCKET_SIZE);
      for (let offsetX = -1; offsetX <= 1; offsetX += 1) {
        for (let offsetZ = -1; offsetZ <= 1; offsetZ += 1) {
          const nearby = this.duckSpatialBuckets.get(duckBucketKey(bucketX + offsetX, bucketZ + offsetZ));
          if (!nearby) continue;
          for (const other of nearby) {
            if (other === duck) continue;
            const awayX = duck.group.position.x - other.group.position.x;
            const awayZ = duck.group.position.z - other.group.position.z;
            const distanceSq = awayX * awayX + awayZ * awayZ;
            if (distanceSq <= 0.001 || distanceSq >= DUCK_SEPARATION_RADIUS * DUCK_SEPARATION_RADIUS) continue;
            const distance = Math.sqrt(distanceSq);
            const force = (DUCK_SEPARATION_RADIUS - distance) / (DUCK_SEPARATION_RADIUS * distance);
            separation.x += awayX * force;
            separation.z += awayZ * force;
          }
        }
      }
      if (nearestDistance > duck.preferredDistance) {
        const steeringTarget = this.duckSteeringTarget.copy(waypoint).add(lateral).addScaledVector(separation, 1.45);
        duck.faceAndMoveToward(
          steeringTarget,
          dt,
          (next) => !this.isBlocked(next, duck.kind === 'boss' ? 1.2 : duck.kind === 'armored' ? 0.72 : 0.58),
          pathWaypoint ? 0.32 : duck.preferredDistance,
        );
      } else {
        duck.faceAndMoveToward(nearest.position, 0, () => false, Number.POSITIVE_INFINITY);
      }

      if (duck.kind === 'medic' && duck.abilityCooldown <= 0) {
        const healed = this.healNearbyDucks(duck);
        duck.abilityCooldown = healed > 0 ? 4.6 : 1.2;
      }
      if (nearestDistance <= duck.attackRange && duck.attackCooldown <= 0 && this.hasGridLineOfSight(duck.group.position, nearest.position)) {
        this.performDuckAttack(duck, nearest, targets);
      }
    }
  }

  private healNearbyDucks(medic: Duck): number {
    let healed = 0;
    for (const duck of this.ducks.values()) {
      if (duck === medic || !duck.alive || duck.group.position.distanceToSquared(medic.group.position) > 7 * 7) continue;
      if (!duck.heal(duck.kind === 'boss' ? 18 : 14)) continue;
      healed += 1;
      this.particles.emit(duck.group.position.clone().setY(1.1), 0x7ce7a2, 5, { speed: 1.2, lift: 2.1, life: 0.55 });
    }
    if (healed > 0) {
      medic.beginAttack();
      this.audio.sfx('quackMedic');
      this.particles.emit(medic.group.position.clone().setY(1.2), 0x7ce7a2, 12, { speed: 2, lift: 3.2, life: 0.75 });
    }
    return healed;
  }

  private performDuckAttack(
    duck: Duck,
    nearest: { id: string; position: THREE.Vector3 },
    targets: Array<{ id: string; position: THREE.Vector3 }>,
  ): void {
    duck.beginAttack();
    const soundByKind = {
      standard: 'quack',
      kamikaze: 'quackKamikaze',
      armored: 'quackArmor',
      medic: 'quackMedic',
      sniper: 'quackSniper',
      boss: 'quackBoss',
    } as const;
    this.audio.sfx(soundByKind[duck.kind]);
    const hitTarget = (target: { id: string; position: THREE.Vector3 }, damage: number): void => {
      const localId = this.online ? this.network?.id : 'local';
      if (target.id === localId) this.applyDamage(damage, duck.group.position);
      else this.network?.send({ type: 'game-event', event: { kind: 'enemy-damage', targetId: target.id, damage } });
    };

    if (duck.kind === 'kamikaze') {
      for (const target of targets) {
        if (target.position.distanceToSquared(duck.group.position) <= duck.attackRange * duck.attackRange) hitTarget(target, duck.damage);
      }
      this.particles.emit(duck.group.position.clone().setY(1), 0xff7338, 34, { speed: 5.2, lift: 3.5, gravity: 3, life: 0.9 });
      duck.hit(duck.maxHealth * 2);
      this.kills += 1;
      this.updateHud();
      return;
    }

    if (duck.kind === 'sniper') {
      this.particles.emit(duck.group.position.clone().setY(1.25), 0x73e5ef, 8, { speed: 3.6, lift: 0.3, gravity: 0.1, life: 0.42 });
      this.particles.emit(nearest.position.clone().setY(1.1), 0x73e5ef, 7, { speed: 1.8, lift: 2.2, life: 0.5 });
      hitTarget(nearest, duck.damage);
      return;
    }

    if (duck.kind === 'boss') {
      for (const target of targets) {
        if (target.position.distanceToSquared(duck.group.position) <= duck.attackRange * duck.attackRange) hitTarget(target, duck.damage);
      }
      this.particles.emit(duck.group.position.clone().setY(2), 0xb45de2, 20, { speed: 4.4, lift: 2.4, gravity: 1.2, life: 0.75 });
      return;
    }

    hitTarget(nearest, duck.damage);
  }

  private updateHorde(now: number): void {
    if (this.mode !== 'horde') return;
    const alive = [...this.ducks.values()].some((duck) => duck.alive);
    if (alive) return;
    if (this.nextHordeWaveAt <= 0) {
      this.nextHordeWaveAt = now + (this.hordeWave === 0 ? 1.8 : 5);
      if (this.hordeWave > 0) {
        this.health = Math.min(100, this.health + 12);
        this.weapons.forEach((weapon) => {
          weapon.reserve = Math.min(weapon.maxReserve, weapon.reserve + Math.ceil(weapon.magazineSize * 0.6));
        });
        this.callbacks.onToast(`ONDATA ${this.hordeWave} BONIFICATA · prossima valvola tra 5 secondi`);
        this.updateHud();
      }
      return;
    }
    if (now < this.nextHordeWaveAt || (this.online && !this.network?.isHost)) return;
    const wave = this.hordeWave + 1;
    this.nextHordeWaveAt = 0;
    if (this.online) this.network?.send({ type: 'game-event', event: { kind: 'horde-wave', wave } });
    this.spawnHordeWave(wave);
  }

  private spawnHordeWave(wave: number): void {
    if (!this.level || this.mode !== 'horde' || this.ducks.has(`horde-${wave}-0`)) return;
    this.cleanupDeflatedDucks();
    const composition = getHordeComposition(wave, this.playerCount);
    const cells = getReinforcementCells(this.level.grid, composition.length, 4000 + wave * 97 + this.playerCount * 13);
    const width = this.level.grid[0].length;
    const height = this.level.grid.length;
    cells.forEach((cell, index) => {
      this.addDuck(`horde-${wave}-${index}`, composition[index], this.cellToWorld(cell.row, cell.col, width, height));
    });
    this.hordeWave = wave;
    this.nextHordeWaveAt = 0;
    this.audio.sfx(wave % 5 === 0 ? 'quackBoss' : 'quackKamikaze');
    this.callbacks.onToast(wave % 5 === 0
      ? `ONDATA ${wave}: IL QUACKODEMON È ENTRATO NELLA CAMERA!`
      : `ONDATA ${wave}: ${composition.length} segnali gommosi in arrivo!`);
    this.updateHud();
  }

  private cleanupDeflatedDucks(): void {
    const removed = new Set<THREE.Object3D>();
    for (const [id, duck] of this.ducks) {
      if (duck.alive) continue;
      removed.add(duck.group);
      this.scene.remove(duck.group);
      this.ducks.delete(id);
    }
    if (removed.size === 0) return;
    for (let index = this.worldRaycast.length - 1; index >= 0; index -= 1) {
      if (removed.has(this.worldRaycast[index])) this.worldRaycast.splice(index, 1);
    }
  }

  private hasGridLineOfSight(from: THREE.Vector3, to: THREE.Vector3): boolean {
    const distance = from.distanceTo(to);
    const steps = Math.max(2, Math.ceil(distance / (CELL * 0.45)));
    for (let index = 1; index < steps; index += 1) {
      const amount = index / steps;
      const cell = this.worldToCell(
        THREE.MathUtils.lerp(from.x, to.x, amount),
        THREE.MathUtils.lerp(from.z, to.z, amount),
      );
      if (!cell || cell.char === '#') return false;
      if (cell.char === '?' && !this.secrets.get(`secret-${this.levelIndex}-${cell.row}-${cell.col}`)?.open) return false;
      if (DOOR_BY_CELL[cell.char] && !this.doors.get(`door-${this.levelIndex}-${cell.row}-${cell.col}`)?.open) return false;
    }
    return true;
  }

  private refreshNavigationField(targets: Array<{ id: string; position: THREE.Vector3 }>): void {
    if (!this.level) return;
    const height = this.level.grid.length;
    const width = this.level.grid[0].length;
    const targetCells = targets
      .map((target) => this.worldToCell(target.position.x, target.position.z))
      .filter((cell): cell is { row: number; col: number; char: string } => Boolean(cell))
      .map(({ row, col }) => ({ row, col }));
    this.navField = buildNavigationField(width, height, targetCells, (row, col) => this.isNavigationCellOpen(row, col));
  }

  private getDuckWaypoint(duck: Duck): THREE.Vector3 | undefined {
    if (!this.level || !this.navField) return undefined;
    const height = this.level.grid.length;
    const width = this.level.grid[0].length;
    const cell = this.worldToCell(duck.group.position.x, duck.group.position.z);
    if (!cell) return undefined;
    const next = getNavigationStep(width, height, this.navField, cell, duck.navigationBias);
    return next ? this.cellToWorld(next.row, next.col, width, height) : undefined;
  }

  private isNavigationCellOpen(row: number, col: number): boolean {
    if (!this.level || row < 0 || row >= this.level.grid.length || col < 0 || col >= this.level.grid[0].length) return false;
    const cell = this.level.grid[row][col];
    if (cell === '#') return false;
    if (cell === '?') return Boolean(this.secrets.get(`secret-${this.levelIndex}-${row}-${col}`)?.open);
    if (!DOOR_BY_CELL[cell]) return true;
    return Boolean(this.doors.get(`door-${this.levelIndex}-${row}-${col}`)?.open);
  }

  private updateExit(time: number): void {
    if (!this.exit || this.mode === 'horde') return;
    this.exit.rotation.y = time * 0.42;
    const exitDistance = Math.hypot(
      this.exit.position.x - this.camera.position.x,
      this.exit.position.z - this.camera.position.z,
    );
    if (exitDistance > 1.55) return;
    const remaining = [...this.ducks.values()].filter((duck) => duck.alive);
    if (remaining.length > 0) {
      if (this.toastCooldown <= 0) {
        const bossAlive = remaining.some((duck) => duck.kind === 'boss');
        this.callbacks.onToast(bossAlive
          ? `Lo scarico è sigillato dal Quackodemon e da altre ${remaining.length - 1} paperelle!`
          : `Bonifica incompleta: restano ${remaining.length} paperelle nell'impianto.`);
        this.toastCooldown = 2.4;
      }
      return;
    }
    this.completeLevel();
  }

  private updateWeapon(time: number, dt: number): void {
    const moving = Math.min(1, Math.hypot(this.velocity.x, this.velocity.z) / 5);
    const motion = this.settings.reducedMotion ? 0 : moving;
    this.recoil = THREE.MathUtils.damp(this.recoil, 0, 15, dt);
    this.weaponRig.position.x = 0.48 + Math.sin(time * 8) * 0.014 * motion;
    this.weaponRig.position.y = -0.47 + Math.abs(Math.cos(time * 8)) * 0.013 * motion;
    this.weaponRig.position.z = -0.96 + this.recoil * 0.12;
    const reloadTilt = this.reloadingWeapon === this.activeWeapon ? -0.62 : 0;
    this.weaponRig.rotation.z = THREE.MathUtils.damp(this.weaponRig.rotation.z, reloadTilt, 11, dt);
    this.weaponRig.rotation.x = THREE.MathUtils.damp(this.weaponRig.rotation.x, this.sprinting ? -0.12 : 0, 9, dt);
  }

  private updateNetwork(now: number): void {
    if (!this.online || !this.network?.connected) return;
    if (now - this.lastNetworkTick >= 0.075) {
      this.lastNetworkTick = now;
      this.network.send({
        type: 'player-state',
        state: {
          x: this.camera.position.x,
          y: this.camera.position.y,
          z: this.camera.position.z,
          yaw: this.camera.rotation.y,
          health: this.health,
          weapon: this.activeWeapon,
          paused: this.paused,
        },
      });
    }
    if (this.network.isHost && now - this.lastDuckTick >= 0.1) {
      this.lastDuckTick = now;
      this.network.send({ type: 'duck-snapshot', ducks: [...this.ducks.values()].map((duck) => duck.toSnapshot()) });
    }
  }

  private shoot(): void {
    if (this.status !== 'playing' || this.paused || document.pointerLockElement !== this.canvas) return;
    const now = performance.now() / 1000;
    const weapon = this.weapons[this.activeWeapon];
    if (now < this.nextShotAt) return;
    if (this.reloadingWeapon >= 0) return;
    if (weapon.magazine <= 0) {
      this.audio.sfx('empty');
      this.callbacks.onToast(weapon.reserve > 0 ? 'Caricatore vuoto: ricarico!' : 'A secco! Cerca una cassetta arancione.');
      this.nextShotAt = now + 0.28;
      if (weapon.reserve > 0) this.startReload();
      return;
    }
    this.nextShotAt = now + weapon.cooldown;
    weapon.magazine -= 1;
    this.recoil = weapon.recoil;
    const soundByMode = { pump: 'plunger', bubble: 'bubble', roll: 'paper', shower: 'shower' } as const;
    this.audio.sfx(soundByMode[weapon.mode]);
    const muzzle = this.camera.position.clone().add(new THREE.Vector3(0.38, -0.22, -0.85).applyEuler(this.camera.rotation));
    this.particles.emit(muzzle, weapon.color, weapon.mode === 'roll' ? 13 : weapon.mode === 'shower' ? 8 : 6, {
      speed: weapon.mode === 'roll' ? 3.8 : weapon.mode === 'shower' ? 3.1 : 2.1,
      lift: 0.7,
      gravity: 1.8,
      life: 0.38,
    });
    if (weapon.mode === 'pump') this.firePump(weapon);
    else if (weapon.mode === 'shower') this.fireShower(weapon);
    else this.firePrecisionWeapon(weapon);
    if (weapon.magazine === 0 && weapon.reserve > 0) this.startReload();
    this.updateHud();
  }

  private firePump(weapon: Weapon): void {
    const offsets = [
      [0, 0], [-0.018, -0.012], [0.018, -0.01], [-0.012, 0.018], [0.012, 0.018],
    ] as const;
    for (const [x, y] of offsets) {
      const hit = this.firstWeaponIntersection(x, y, weapon.range);
      const duckId = hit?.object.userData.duckId as string | undefined;
      if (duckId) this.hitDuck(duckId, weapon.damage, true);
      if (hit) this.particles.emit(hit.point, 0xf06a2b, 2, { speed: 1.3, lift: 1, life: 0.28 });
    }
  }

  private firePrecisionWeapon(weapon: Weapon): void {
    const first = this.firstWeaponIntersection(0, 0, weapon.range);
    if (!first) return;
    const duckId = first.object.userData.duckId as string | undefined;
    if (duckId) this.hitDuck(duckId, weapon.damage, true);
    if (weapon.mode === 'roll') {
      this.particles.emit(first.point, 0xe7f2ee, 28, { speed: 4.6, lift: 3.8, gravity: 2.2, life: 1.05 });
      for (const duck of this.ducks.values()) {
        if (duck.id !== duckId && duck.alive && duck.group.position.distanceTo(first.point) < 5.1) {
          this.hitDuck(duck.id, Math.round(weapon.damage * 0.52), true);
        }
      }
    } else {
      this.particles.emit(first.point, weapon.color, 7, { speed: 1.7, lift: 1.8, life: 0.42 });
    }
  }

  private fireShower(weapon: Weapon): void {
    const forward = new THREE.Vector3(0, 0, -1).applyEuler(this.camera.rotation).normalize();
    let hitCount = 0;
    for (const duck of this.ducks.values()) {
      if (!duck.alive) continue;
      const toDuck = duck.group.position.clone().sub(this.camera.position);
      const distance = toDuck.length();
      if (distance > weapon.range || distance < 0.01) continue;
      toDuck.normalize();
      if (toDuck.dot(forward) < 0.955 || !this.hasGridLineOfSight(this.camera.position, duck.group.position)) continue;
      this.hitDuck(duck.id, weapon.damage, true);
      hitCount += 1;
    }
    const sprayPoint = this.camera.position.clone().addScaledVector(forward, 3.2).setY(this.camera.position.y - 0.2);
    this.particles.emit(sprayPoint, weapon.color, Math.min(14, 6 + hitCount * 2), { speed: 2.8, lift: 0.4, gravity: 2.4, life: 0.5 });
  }

  private firstWeaponIntersection(x: number, y: number, range: number): THREE.Intersection<THREE.Object3D> | undefined {
    this.raycaster.setFromCamera(new THREE.Vector2(x, y), this.camera);
    this.raycaster.far = range;
    return this.raycaster.intersectObjects(this.worldRaycast, true).find((hit) => {
      const duckId = hit.object.userData.duckId as string | undefined;
      return !duckId || Boolean(this.ducks.get(duckId)?.alive);
    });
  }

  private startReload(): void {
    if (this.status !== 'playing' || this.paused) return;
    const weapon = this.weapons[this.activeWeapon];
    if (this.reloadingWeapon >= 0 || weapon.magazine >= weapon.magazineSize || weapon.reserve <= 0) return;
    this.reloadingWeapon = this.activeWeapon;
    this.reloadEndsAt = performance.now() / 1000 + weapon.reloadSeconds;
    this.audio.sfx('reload');
    this.callbacks.onToast(`Ricarico ${weapon.name.toLowerCase()}…`);
    this.updateHud();
  }

  private hitDuck(id: string, damage: number, announce: boolean): void {
    const duck = this.ducks.get(id);
    if (!duck?.alive) return;
    this.callbacks.onHit();
    if (this.online && !this.network?.isHost) {
      if (announce) this.network?.send({ type: 'game-event', event: { kind: 'enemy-hit', id, damage, shooterId: this.network.id } });
      return;
    }
    const died = duck.hit(damage);
    this.particles.emit(
      duck.group.position.clone().setY(duck.kind === 'boss' ? 1.8 : 1),
      duck.kind === 'sniper' ? 0x70d9d4 : duck.kind === 'kamikaze' ? 0xff6a36 : duck.kind === 'medic' ? 0x7ce7a2 : 0xf5cc28,
      died ? (duck.kind === 'boss' ? 42 : 20) : 7,
      { speed: died ? 4.2 : 2.2, lift: died ? 4 : 2.1, life: died ? 1.05 : 0.5 },
    );
    if (died) {
      this.kills += 1;
      this.audio.sfx('quack');
      this.callbacks.onToast(duck.kind === 'boss' ? 'IL QUACKODEMON È STATO STAPPATO!' : `${duck.profile.label} sgonfiata!`);
    }
    if (announce && this.online) {
      this.network?.send({ type: 'game-event', event: { kind: 'enemy-hit', id, damage, shooterId: this.network.id } });
    }
    this.updateHud();
  }

  private getInteractionPrompt(): HudState['interaction'] {
    if (this.status !== 'playing' || this.paused || this.health <= 0) return undefined;
    const downedTeammate = [...this.remotePlayers.values()]
      .filter((remote) => remote.state.health <= 0)
      .sort((a, b) => a.group.position.distanceToSquared(this.camera.position) - b.group.position.distanceToSquared(this.camera.position))[0];
    if (downedTeammate && downedTeammate.group.position.distanceToSquared(this.camera.position) <= 3.2 * 3.2) {
      return { key: 'E', label: `RIANIMA ${downedTeammate.state.nickname.toUpperCase()}` };
    }

    const forward = new THREE.Vector3(0, 0, -1).applyEuler(this.camera.rotation);
    for (const secret of this.secrets.values()) {
      if (secret.open) continue;
      const delta = secret.mesh.position.clone().sub(this.camera.position).setY(0);
      if (delta.length() < 3.2 && delta.normalize().dot(forward) > 0.42) {
        return { key: 'E', label: 'AZIONA LA VALVOLA SOSPETTA' };
      }
    }

    if (this.elevatorCooldown <= 0 && this.elevators.some((candidate) => candidate.position.distanceTo(this.camera.position) < 1.8)) {
      return { key: 'E', label: 'USA IL MONTACARICHI' };
    }

    let chosen: DoorState | undefined;
    let best = 3.4;
    for (const door of this.doors.values()) {
      if (door.open) continue;
      const delta = door.mesh.position.clone().sub(this.camera.position).setY(0);
      const distance = delta.length();
      if (distance < best && delta.normalize().dot(forward) > 0.45) {
        chosen = door;
        best = distance;
      }
    }
    if (!chosen) return undefined;
    if (!this.keysOwned.has(chosen.color)) {
      return { label: `CHIAVE ${this.keyLabel(chosen.color).toUpperCase()} NECESSARIA`, blocked: true };
    }
    return { key: 'E', label: 'APRI LA PARATIA' };
  }

  private interact(): void {
    if (this.status !== 'playing') return;
    const downedTeammate = [...this.remotePlayers.values()]
      .filter((remote) => remote.state.health <= 0)
      .sort((a, b) => a.group.position.distanceToSquared(this.camera.position) - b.group.position.distanceToSquared(this.camera.position))[0];
    if (downedTeammate && downedTeammate.group.position.distanceToSquared(this.camera.position) <= 3.2 * 3.2) {
      this.reviveRemote(downedTeammate);
      return;
    }
    const forward = new THREE.Vector3(0, 0, -1).applyEuler(this.camera.rotation);
    let nearestSecret: SecretState | undefined;
    let secretDistance = 3.2;
    for (const secret of this.secrets.values()) {
      if (secret.open) continue;
      const delta = secret.mesh.position.clone().sub(this.camera.position).setY(0);
      const distance = delta.length();
      if (distance < secretDistance && delta.normalize().dot(forward) > 0.42) {
        nearestSecret = secret;
        secretDistance = distance;
      }
    }
    if (nearestSecret) {
      this.openSecret(nearestSecret.id, true);
      return;
    }

    if (this.elevatorCooldown <= 0) {
      const elevator = this.elevators.find((candidate) => candidate.position.distanceTo(this.camera.position) < 1.8);
      if (elevator) {
        this.useElevator(elevator);
        return;
      }
    }

    let chosen: DoorState | undefined;
    let best = 3.4;
    for (const door of this.doors.values()) {
      if (door.open) continue;
      const delta = door.mesh.position.clone().sub(this.camera.position);
      delta.y = 0;
      const distance = delta.length();
      if (distance < best && delta.normalize().dot(forward) > 0.45) {
        chosen = door;
        best = distance;
      }
    }
    if (!chosen) return;
    if (!this.keysOwned.has(chosen.color)) {
      this.audio.sfx('empty');
      this.callbacks.onToast(`Serve la chiave ${this.keyLabel(chosen.color)}.`);
      return;
    }
    this.openDoor(chosen.id, true);
  }

  private openSecret(id: string, announce: boolean): void {
    const secret = this.secrets.get(id);
    if (!secret || secret.open) return;
    secret.open = true;
    this.secretsFound += 1;
    this.navField = undefined;
    this.nextNavRefreshAt = 0;
    this.minimapRevision += 1;
    this.audio.sfx('door');
    this.particles.emit(secret.mesh.position.clone().setY(1.2), 0x895fd9, 20, { speed: 2.6, lift: 3.4, life: 0.85 });
    if (announce) {
      this.callbacks.onToast(`PASSAGGIO SEGRETO ${this.secretsFound}! Il muro aveva una valvola sospetta.`);
      if (this.online) this.network?.send({ type: 'game-event', event: { kind: 'secret', id } });
    }
  }

  private reviveRemote(remote: RemotePlayer): void {
    if (!this.online || remote.state.health > 0) return;
    remote.state = { ...remote.state, health: 55 };
    remote.group.visible = true;
    this.revives += 1;
    this.audio.sfx('pickup');
    this.particles.emit(remote.group.position.clone().setY(1), 0x70d79a, 18, { speed: 2.2, lift: 3.6, life: 0.85 });
    this.network?.send({
      type: 'game-event',
      event: { kind: 'revive', targetId: remote.state.id, helperId: this.network.id },
    });
    this.callbacks.onToast(`${remote.state.nickname} rianimato con una dose di sapone!`);
    this.updateHud();
  }

  private reviveLocal(): void {
    if (this.health > 0) return;
    this.health = 55;
    this.reviveDeadline = 0;
    this.audio.sfx('pickup');
    this.particles.emit(this.camera.position.clone().setY(0.8), 0x70d79a, 18, { speed: 2.2, lift: 3.6, life: 0.85 });
    this.callbacks.onToast('Un compagno ti ha rimesso in piedi!');
    this.updateHud();
    this.requestPointerLock();
  }

  private useElevator(source: ElevatorState): void {
    if (this.elevators.length < 2) return;
    const destination = this.elevators.find((elevator) => elevator !== source);
    if (!destination) return;
    this.elevatorCooldown = 1.2;
    this.audio.sfx('door');
    this.particles.emit(source.position.clone().setY(0.2), 0x58c9d4, 14, { speed: 2.4, lift: 4, life: 0.7 });
    this.camera.position.copy(destination.position);
    const forward = new THREE.Vector3(0, 0, -1).applyEuler(this.camera.rotation).multiplyScalar(1.65);
    const exitPosition = destination.position.clone().add(forward).setY(EYE_HEIGHT);
    if (!this.isBlocked(exitPosition, PLAYER_RADIUS)) this.camera.position.copy(exitPosition);
    this.velocity.set(0, 0, 0);
    this.revealMinimap(this.camera.position, 5);
    this.particles.emit(destination.position.clone().setY(0.2), 0x58c9d4, 14, { speed: 2.4, lift: 4, life: 0.7 });
    this.callbacks.onToast('MONTACARICHI IDRAULICO: nuovo settore raggiunto.');
  }

  private openDoor(id: string, announce: boolean): void {
    const door = this.doors.get(id);
    if (!door || door.open) return;
    door.open = true;
    this.audio.sfx('door');
    this.particles.emit(door.mesh.position.clone().setY(1.1), 0xe46d2f, 16, { speed: 2.5, lift: 3, life: 0.9 });
    if (announce) {
      this.callbacks.onToast('Paratia aperta. Avanti, squadra papera!');
      if (this.online) this.network?.send({ type: 'game-event', event: { kind: 'door', id } });
    }
    this.updateHud();
  }

  private applyDamage(amount: number, source?: THREE.Vector3): void {
    if (!canReceiveDamage(this.status, this.paused) || this.health <= 0) return;
    const actualDamage = Math.min(this.health, amount);
    this.health = Math.max(0, this.health - amount);
    this.damageTaken += actualDamage;
    this.audio.sfx('hurt');
    this.callbacks.onDamage(source
      ? classifyDamageDirection(
        this.camera.rotation.y,
        source.x - this.camera.position.x,
        source.z - this.camera.position.z,
      )
      : 'environment');
    this.updateHud();
    if (this.health > 0) return;
    if (this.online) {
      this.reviveDeadline = performance.now() / 1000 + 12;
      this.velocity.set(0, 0, 0);
      this.callbacks.onToast('SEI A TERRA! Un compagno può rianimarti con E. Respawn tra 12 secondi.');
    } else {
      this.finish(false);
    }
  }

  private completeLevel(): void {
    const next = this.levelIndex + 1;
    if (this.online && !this.network?.isHost) {
      if (this.exitRequested) return;
      this.exitRequested = true;
      this.network?.send({ type: 'game-event', event: { kind: 'exit-request', index: this.levelIndex } });
      this.callbacks.onToast('Uscita raggiunta: attendiamo il caposquadra…');
      return;
    }
    if (this.online) this.network?.send({ type: 'game-event', event: { kind: 'level', index: next } });
    this.loadLevel(next);
  }

  private finish(victory: boolean): void {
    this.status = 'ended';
    this.paused = false;
    this.audio.stopMusic();
    if (victory) this.audio.sfx('win');
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
    const elapsedSeconds = (performance.now() - this.startedAt) / 1000;
    this.callbacks.onResult({
      victory,
      elapsedSeconds,
      kills: this.kills,
      secrets: this.secretsFound,
      revives: this.revives,
      score: calculateScore({
        kills: this.kills,
        elapsedSeconds,
        secrets: this.secretsFound,
        revives: this.revives,
        difficulty: this.settings.difficulty,
        victory,
      }) + (this.mode === 'horde' ? this.hordeWave * 1_200 : 0),
      difficulty: this.settings.difficulty,
      mode: this.mode,
      wave: this.hordeWave,
      damageTaken: this.damageTaken,
    });
  }

  private switchWeapon(index: number): void {
    if (!this.weapons[index] || index === this.activeWeapon) return;
    this.reloadingWeapon = -1;
    this.reloadEndsAt = 0;
    this.activeWeapon = index;
    this.audio.sfx('click');
    this.buildWeapon();
    this.updateHud();
  }

  private updateHud(): void {
    const weapon = this.weapons[this.activeWeapon];
    const ducksLeft = [...this.ducks.values()].filter((duck) => duck.alive).length;
    const boss = [...this.ducks.values()].find((duck) => duck.alive && duck.kind === 'boss');
    const elapsedSeconds = Math.max(0, (performance.now() - this.startedAt) / 1000);
    const requiredKeys = new Set(
      [...(this.level?.grid.join('') ?? '')]
        .map((cell) => KEY_BY_CELL[cell])
        .filter((color): color is KeyColor => Boolean(color)),
    );
    const hasAllKeys = [...requiredKeys].every((color) => this.keysOwned.has(color));
    const objective = this.mode === 'horde'
      ? ducksLeft > 0
        ? `ONDATA ${this.hordeWave} · segnali gommosi rimasti: ${ducksLeft}`
        : this.hordeWave === 0
          ? 'Preparati: la prima valvola sta per aprirsi'
          : `Ondata ${this.hordeWave} ripulita · ricarica e respira`
      : hasAllKeys
        ? ducksLeft > 0
          ? `Bonifica le paperelle rimaste: ${ducksLeft}`
          : 'Impianto pulito: raggiungi lo scarico'
        : this.level?.objective ?? '';
    this.callbacks.onHud({
      health: this.health,
      weapon: weapon.name,
      ammo: `${weapon.magazine} / ${weapon.reserve}`,
      stamina: this.stamina,
      movementState: this.slowedByPuddle
        ? 'slowed'
        : this.sprinting
          ? 'sprinting'
          : this.stamina < 12
            ? 'tired'
            : 'ready',
      reloading: this.reloadingWeapon === this.activeWeapon,
      keys: [...this.keysOwned],
      ducksLeft,
      objective,
      mapIndex: this.levelIndex,
      mode: this.mode,
      wave: this.hordeWave,
      elapsedSeconds,
      score: calculateScore({
        kills: this.kills,
        elapsedSeconds,
        secrets: this.secretsFound,
        revives: this.revives,
        difficulty: this.settings.difficulty,
        victory: false,
      }) + (this.mode === 'horde' ? this.hordeWave * 1_200 : 0),
      interaction: this.getInteractionPrompt(),
      boss: boss ? { label: 'QUACKODEMON', health: boss.health, maxHealth: boss.maxHealth } : undefined,
      teammates: [...this.remotePlayers.values()].map((remote) => ({
        id: remote.state.id,
        nickname: remote.state.nickname,
        color: remote.state.color,
        health: remote.state.health,
        paused: remote.state.paused,
      })),
      minimap: this.getMinimapState(),
    });
  }

  private revealMinimap(position: THREE.Vector3, radius: number): void {
    if (!this.level || this.minimapExplored.length === 0) return;
    const center = this.worldToCell(position.x, position.z);
    if (!center) return;
    const height = this.level.grid.length;
    const width = this.level.grid[0].length;
    let changed = false;
    for (let row = Math.max(0, center.row - radius); row <= Math.min(height - 1, center.row + radius); row += 1) {
      for (let col = Math.max(0, center.col - radius); col <= Math.min(width - 1, center.col + radius); col += 1) {
        const rowDistance = row - center.row;
        const colDistance = col - center.col;
        if (rowDistance * rowDistance + colDistance * colDistance > radius * radius) continue;
        const index = row * width + col;
        if (this.minimapExplored[index]) continue;
        this.minimapExplored[index] = 1;
        changed = true;
      }
    }
    if (changed) this.minimapRevision += 1;
  }

  private getMinimapState(): MinimapState | undefined {
    if (!this.level || this.minimapExplored.length === 0) return undefined;
    const playerCell = this.worldToCell(this.camera.position.x, this.camera.position.z);
    if (!playerCell) return undefined;
    const keys = [...this.pickups.values()]
      .filter((pickup): pickup is PickupState & { key: KeyColor } => pickup.active && pickup.kind === 'key' && Boolean(pickup.key))
      .map((pickup) => ({ row: pickup.row, col: pickup.col, color: pickup.key }));
    const teammates = [...this.remotePlayers.values()]
      .filter((remote) => remote.state.health > 0)
      .flatMap((remote) => {
        const cell = this.worldToCell(remote.group.position.x, remote.group.position.z);
        return cell ? [{ row: cell.row, col: cell.col, color: remote.state.color }] : [];
      });
    return {
      grid: this.level.grid,
      explored: this.minimapExplored,
      revision: this.minimapRevision,
      player: { row: playerCell.row, col: playerCell.col, yaw: this.camera.rotation.y },
      doors: [...this.doors.values()].map((door) => ({ row: door.row, col: door.col, color: door.color, open: door.open })),
      secrets: [...this.secrets.values()].map((secret) => ({ row: secret.row, col: secret.col, open: secret.open })),
      keys,
      teammates,
    };
  }

  private attachNetwork(network: NetworkClient): void {
    this.networkDisposers.push(
      network.on<Extract<ServerMessage, { type: 'player-state' }>>('player-state', (message) => this.updateRemotePlayer(message.player)),
      network.on<Extract<ServerMessage, { type: 'player-left' }>>('player-left', (message) => this.removeRemotePlayer(message.id)),
      network.on<Extract<ServerMessage, { type: 'duck-snapshot' }>>('duck-snapshot', (message) => {
        if (network.isHost) return;
        for (const snapshot of message.ducks) this.ducks.get(snapshot.id)?.applySnapshot(snapshot);
        this.updateHud();
      }),
      network.on<Extract<ServerMessage, { type: 'game-event' }>>('game-event', (message) => this.handleGameEvent(message.event)),
    );
  }

  private detachNetwork(): void {
    for (const dispose of this.networkDisposers.splice(0)) dispose();
    this.network = undefined;
    this.online = false;
  }

  private handleGameEvent(event: GameEvent): void {
    if (event.kind === 'enemy-hit' && this.network?.isHost) {
      this.hitDuck(event.id, event.damage, false);
    } else if (event.kind === 'enemy-damage' && event.targetId === this.network?.id) {
      this.applyDamage(event.damage);
    } else if (event.kind === 'pickup') {
      const pickup = this.pickups.get(event.id);
      if (pickup) {
        if (event.key) this.keysOwned.add(event.key as KeyColor);
        this.collectPickup(pickup, false);
      }
    } else if (event.kind === 'door') {
      this.openDoor(event.id, false);
    } else if (event.kind === 'secret') {
      this.openSecret(event.id, false);
    } else if (event.kind === 'level' && event.index !== this.levelIndex) {
      this.loadLevel(event.index);
    } else if (event.kind === 'horde-wave') {
      this.spawnHordeWave(event.wave);
    } else if (event.kind === 'exit-request' && this.network?.isHost && event.index === this.levelIndex) {
      this.completeLevel();
    } else if (event.kind === 'revive') {
      if (event.targetId === this.network?.id) this.reviveLocal();
      const remote = this.remotePlayers.get(event.targetId);
      if (remote) {
        remote.state = { ...remote.state, health: 55 };
        remote.group.visible = true;
      }
      this.updateHud();
    } else if (event.kind === 'respawn') {
      const remote = this.remotePlayers.get(event.playerId);
      if (remote) {
        remote.state = { ...remote.state, health: 100 };
        remote.group.visible = true;
      }
      this.updateHud();
    }
  }

  private updateRemotePlayer(state: PlayerNetState): void {
    if (state.id === this.network?.id) return;
    let remote = this.remotePlayers.get(state.id);
    if (!remote) {
      remote = { state, group: this.createRemotePlayer(state.color) };
      remote.group.position.set(state.x, 0, state.z);
      this.remotePlayers.set(state.id, remote);
      this.scene.add(remote.group);
    } else {
      remote.group.position.lerp(new THREE.Vector3(state.x, 0, state.z), 0.5);
    }
    remote.state = state;
    remote.group.rotation.y = state.yaw;
    const overlapX = remote.group.position.x - this.camera.position.x;
    const overlapZ = remote.group.position.z - this.camera.position.z;
    remote.group.visible = overlapX * overlapX + overlapZ * overlapZ > 1;
    remote.group.scale.y = THREE.MathUtils.damp(remote.group.scale.y, state.health > 0 ? 1 : 0.32, 8, 0.075);
    remote.group.rotation.z = THREE.MathUtils.damp(remote.group.rotation.z, state.health > 0 ? 0 : Math.PI / 2, 8, 0.075);
    if (state.health > 0) this.revealMinimap(remote.group.position, 4);
  }

  private removeRemotePlayer(id: string): void {
    const remote = this.remotePlayers.get(id);
    if (remote) this.scene.remove(remote.group);
    this.remotePlayers.delete(id);
  }

  private createRemotePlayer(color: PlayerColor): THREE.Group {
    return createPlayerModel(color);
  }

  private getTargets(): Array<{ id: string; position: THREE.Vector3 }> {
    const localId = this.online ? this.network?.id ?? 'local' : 'local';
    const targets: Array<{ id: string; position: THREE.Vector3 }> = [];
    if (isPlayerTargetable(this.health, this.paused)) {
      targets.push({ id: localId, position: this.camera.position.clone() });
    }
    for (const remote of this.remotePlayers.values()) {
      if (isPlayerTargetable(remote.state.health, remote.state.paused)) {
        targets.push({ id: remote.state.id, position: remote.group.position.clone().setY(EYE_HEIGHT) });
      }
    }
    return targets;
  }

  private isBlocked(position: THREE.Vector3, radius: number): boolean {
    if (!this.level) return true;
    const offsets = [[-radius, -radius], [radius, -radius], [-radius, radius], [radius, radius], [0, 0]];
    return offsets.some(([dx, dz]) => {
      const cell = this.worldToCell(position.x + dx, position.z + dz);
      if (!cell) return true;
      if (cell.char === '#') return true;
      if (cell.char === '?') return !this.secrets.get(`secret-${this.levelIndex}-${cell.row}-${cell.col}`)?.open;
      const doorColor = DOOR_BY_CELL[cell.char];
      if (!doorColor) return false;
      const door = this.doors.get(`door-${this.levelIndex}-${cell.row}-${cell.col}`);
      return !door?.open;
    });
  }

  private cellToWorld(row: number, col: number, width: number, height: number): THREE.Vector3 {
    return new THREE.Vector3((col - (width - 1) / 2) * CELL, 0, (row - (height - 1) / 2) * CELL);
  }

  private worldToCell(x: number, z: number): { row: number; col: number; char: string } | undefined {
    if (!this.level) return undefined;
    const height = this.level.grid.length;
    const width = this.level.grid[0].length;
    const col = Math.round(x / CELL + (width - 1) / 2);
    const row = Math.round(z / CELL + (height - 1) / 2);
    if (row < 0 || row >= height || col < 0 || col >= width) return undefined;
    return { row, col, char: this.level.grid[row][col] };
  }

  private keyLabel(color: KeyColor): string {
    return color === 'yellow' ? 'gialla' : color === 'cyan' ? 'ciano' : 'rossa';
  }

  private bindInput(): void {
    window.addEventListener('resize', () => this.resize());
    window.addEventListener('keydown', (event) => {
      this.keysDown.add(event.code);
      if (event.code === 'Digit1') this.switchWeapon(0);
      if (event.code === 'Digit2') this.switchWeapon(1);
      if (event.code === 'Digit3') this.switchWeapon(2);
      if (event.code === 'Digit4') this.switchWeapon(3);
      if (event.code === 'KeyE' && !event.repeat) this.interact();
      if (event.code === 'KeyR' && !event.repeat) this.startReload();
    });
    window.addEventListener('keyup', (event) => this.keysDown.delete(event.code));
    window.addEventListener('mousemove', (event) => {
      if (document.pointerLockElement !== this.canvas || this.status !== 'playing') return;
      const factor = 0.0018 * this.settings.sensitivity;
      this.camera.rotation.y -= event.movementX * factor;
      this.camera.rotation.x = THREE.MathUtils.clamp(this.camera.rotation.x - event.movementY * factor, -1.45, 1.45);
    });
    window.addEventListener('mousedown', (event) => {
      if (event.button === 0) this.shoot();
    });
    window.addEventListener('wheel', (event) => {
      if (this.status !== 'playing') return;
      const direction = event.deltaY > 0 ? 1 : -1;
      this.switchWeapon((this.activeWeapon + direction + this.weapons.length) % this.weapons.length);
    }, { passive: true });
    document.addEventListener('pointerlockchange', () => {
      if (this.status !== 'playing' || document.pointerLockElement === this.canvas) return;
      this.keysDown.clear();
      this.paused = true;
      this.callbacks.onPause(!this.online);
    });
    this.canvas.addEventListener('click', () => {
      if (this.status === 'playing' && document.pointerLockElement !== this.canvas) this.requestPointerLock();
    });
  }

  private resize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(Math.floor(width * this.renderScale), Math.floor(height * this.renderScale), false);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
}
